import React, { useState, useEffect } from 'react';
import { Modal, message, Select } from 'antd';
import styled from 'styled-components';
import { XIcon } from '@heroicons/react/outline';

import { ResearchTableView } from './ResearchTableView';
import { TemplateTable } from './TemplateTable';
import { CellDetailsSidebar } from './CellDetailsSidebar';
import { TemplateService } from './templateEngine';
import type { TableColumn, TableRow, CellData, DataType } from './types';
import type { Creator, CreatorPlatformType } from '../Creators/creatorTypes';
import creatorService, { addCreatorToProject, removeCreatorFromProject } from '../Creators/creatorService';
import { CreatorModal } from '../Creators/CreatorModal';

import { researchAIService, type CreatorInfo } from '../../services/researchAIService';
import { researchTableService, type ResearchTable as ResearchTableType, type ResearchTableConfig, type CreatorCellData } from '../../services/researchTableService';
import { workflowAnalysisQueue } from '../../services/workflowAnalysisQueue';
import { userService } from '../../services/userService';
import { TableType, parseTableType } from '../../types/tableTypes';
import { outlookThreadsService } from '../../services/outlookThreadsService';
import { exportTableToCSV, downloadCSV, copyToClipboard, generateExportFilename } from '../../utils/csvExport';
import { shopifyStatusService } from '../../services/shopifyStatusService';
import { shopifyActionsService } from '../../services/shopifyActionsService';
import { generateCodeName } from './utils/discountCodeGenerator';

// Helper function to get creator's primary platform data
const getCreatorPrimaryPlatform = (creator: Creator): {
  platform: CreatorPlatformType | 'unknown';
  handle: string;
  name: string;
  followers_count?: number;
  engagement_rate?: number;
  profile_image_url?: string;
} => {
  // Check TikTok data first
  if (creator.tiktok_data && creator.tiktok_data.length > 0) {
    const data = creator.tiktok_data[0];
    return {
      platform: 'tiktok',
      handle: data.handle,
      name: data.handle,
      followers_count: data.followers_count,
      engagement_rate: data.engagement_rate,
      profile_image_url: data.profile_image_url
    };
  }
  
  // Check Instagram data
  if (creator.instagram_data && creator.instagram_data.length > 0) {
    const data = creator.instagram_data[0];
    return {
      platform: 'instagram',
      handle: data.handle,
      name: data.handle,
      followers_count: data.followers_count,
      engagement_rate: data.engagement_rate,
      profile_image_url: data.profile_image_url
    };
  }
  
  // Check Twitch data
  if (creator.twitch_data && creator.twitch_data.length > 0) {
    const data = creator.twitch_data[0];
    return {
      platform: 'twitch',
      handle: data.handle,
      name: data.handle,
      followers_count: data.followers_count,
      engagement_rate: undefined,
      profile_image_url: data.profile_image_url
    };
  }
  
  // Fallback to creator shipping data
  return {
    platform: 'unknown',
    handle: creator.creator_shipping?.name || 'Unknown',
    name: creator.creator_shipping?.name || 'Unknown Creator'
  };
};



const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  align-items: stretch;
  position: relative;
  overflow: hidden;
  max-width: 100%;
  width: 100%;
  min-width: 0;
`;


interface ResearchTableProps {
  onBack?: () => void;
  tableId?: string; // Optional table ID for loading existing table
  projectId?: string; // Optional project ID for table switching
  uploadMode?: boolean; // Special mode for Excel upload flow
  searchMode?: boolean; // Special mode for creator search flow
  // Tab bar props
  projectTables?: ResearchTableType[];
  activeTableId?: string | null;
  onTabClick?: (tableId: string) => void;
  onDeleteTable?: (tableId: string) => void;
  onCreateTable?: () => void;
  showTabBar?: boolean;
  // Sidebar responsiveness
  librarySidebarOpen?: boolean;
  sidebarCollapsed?: boolean;
  mainSidebarExpanded?: boolean;
  // Action triggers
  exportTrigger?: number;
  copyTrigger?: number;
  refreshTrigger?: number;
  runAllTrigger?: number;
  runUnrunTrigger?: number;
  analyzeOutreachTrigger?: { templateId: string; timestamp: number } | null;
  // Connection status
  hasOutlookConnected?: boolean;
  hasShopifyConnected?: boolean;
  // Pending change handlers
  onAcceptPendingChange?: (rowId: string, columnId: string) => void;
  onDismissPendingChange?: (rowId: string, columnId: string) => void;
}

// Cell value formatting and validation utilities
const formatCellValue = (value: string, dataType?: DataType): string => {
  if (!value || !dataType) return value;
  
  const cleanValue = value.trim();
  
  switch (dataType) {
    case 'currency':
      // Remove existing currency symbols and ensure proper format
      const currencyMatch = cleanValue.match(/[\d.,]+/);
      if (currencyMatch) {
        const number = parseFloat(currencyMatch[0].replace(/,/g, ''));
        if (!isNaN(number)) {
          return `$${number.toLocaleString()}`;
        }
      }
      return cleanValue.startsWith('$') ? cleanValue : `$${cleanValue}`;
      
    case 'percentage':
      // Remove existing % and ensure proper format
      const percentMatch = cleanValue.match(/[\d.,]+/);
      if (percentMatch) {
        const number = parseFloat(percentMatch[0].replace(/,/g, ''));
        if (!isNaN(number)) {
          // Always treat the number as the final percentage value
          // The AI should return the actual percentage (e.g., 2.5 for 2.5%)
          return `${number.toFixed(1)}%`;
        }
      }
      return cleanValue.endsWith('%') ? cleanValue : `${cleanValue}%`;
      
    case 'number':
      // Ensure it's a valid number
      const number = parseFloat(cleanValue.replace(/,/g, ''));
      if (!isNaN(number)) {
        return number.toLocaleString();
      }
      return cleanValue;
      
    case 'boolean':
      // Normalize boolean values
      const lowerValue = cleanValue.toLowerCase();
      if (['yes', 'true', '1', 'y'].includes(lowerValue)) return 'Yes';
      if (['no', 'false', '0', 'n'].includes(lowerValue)) return 'No';
      return cleanValue;
      
    case 'text':
    default:
      return cleanValue;
  }
};

const validateCellValue = (value: string, dataType?: DataType): { isValid: boolean; errorMessage?: string } => {
  if (!value || !dataType) return { isValid: true };
  
  const cleanValue = value.trim();
  
  switch (dataType) {
    case 'currency':
      const currencyMatch = cleanValue.match(/^\$?[\d.,]+$/);
      if (!currencyMatch) {
        return { isValid: false, errorMessage: 'Currency must be a number (e.g., $100 or 100)' };
      }
      break;
      
    case 'percentage':
      const percentMatch = cleanValue.match(/^[\d.,]+%?$/);
      if (!percentMatch) {
        return { isValid: false, errorMessage: 'Percentage must be a number (e.g., 85% or 85)' };
      }
      break;
      
    case 'number':
      const number = parseFloat(cleanValue.replace(/,/g, ''));
      if (isNaN(number)) {
        return { isValid: false, errorMessage: 'Must be a valid number' };
      }
      break;
      
    case 'boolean':
      const lowerValue = cleanValue.toLowerCase();
      const validBooleans = ['yes', 'no', 'true', 'false', '1', '0', 'y', 'n'];
      if (!validBooleans.includes(lowerValue)) {
        return { isValid: false, errorMessage: 'Must be Yes/No, True/False, or 1/0' };
      }
      break;
      
    case 'text':
    default:
      break;
  }
  
  return { isValid: true };
};

// Helper function to determine research data sources based on creator platform
const getDefaultResearchSources = (platform: string, creator?: Creator, column?: TableColumn) => {
  // If column has specific platform context, respect them
  if (column?.platform_resources && column.platform_resources.length > 0) {
    const sources: string[] = [];
    column.platform_resources.forEach((resource: any) => {
      if (resource.platform === 'tiktok') {
        sources.push('tiktok_database', 'tiktok_analytics');
      } else if (resource.platform === 'instagram') {
        sources.push('instagram_database', 'instagram_analytics');
      }
    });
    return sources;
  }

  // Fallback to original logic for columns without specific platform context
  // If creator is provided, check if they have multiple platforms
  if (creator) {
    const platforms = [];
    if (creator.tiktok_data && creator.tiktok_data.length > 0) platforms.push('tiktok');
    if (creator.instagram_data && creator.instagram_data.length > 0) platforms.push('instagram');
    
    if (platforms.length > 1) {
      // Multi-platform creator - include both TikTok and Instagram sources
      return ['tiktok_database', 'tiktok_analytics', 'instagram_database', 'instagram_analytics'];
    }
  }
  
  switch (platform.toLowerCase()) {
    case 'tiktok':
      return ['tiktok_database', 'tiktok_analytics'];
    case 'instagram':
      return ['instagram_database', 'instagram_analytics'];
    default:
      return ['tiktok_database', 'instagram_database']; // Try both for unknown platforms
  }
};

export const ResearchTable: React.FC<ResearchTableProps> = ({ 
  onBack, 
  tableId, 
  projectId,
  uploadMode = false,
  searchMode = false,
  projectTables = [],
  activeTableId,
  onTabClick,
  onDeleteTable,
  onCreateTable,
  showTabBar = false,
  librarySidebarOpen = false,
  sidebarCollapsed = false,
  mainSidebarExpanded = false,
  exportTrigger = 0,
  copyTrigger = 0,
  refreshTrigger = 0,
  runAllTrigger = 0,
  runUnrunTrigger = 0,
  analyzeOutreachTrigger = null,
  hasOutlookConnected = false,
  hasShopifyConnected = false,
  onAcceptPendingChange,
  onDismissPendingChange
}) => {
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [rows, setRows] = useState<TableRow[]>([]);
  const [tableData, setTableData] = useState<Record<string, Record<string, CellData>>>({});
  const [currentTable, setCurrentTable] = useState<ResearchTableType | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  
  // Creator chat state
  const [outreachSidebarOpen, setOutreachSidebarOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [selectedOutreachColumn, setSelectedOutreachColumn] = useState<TableColumn | null>(null);
  
  // Cell details sidebar state
  const [cellSidebarOpen, setCellSidebarOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{ data: CellData; column: TableColumn; creator?: Creator } | null>(null);
  
  
  // Upload mode state
  const [uploadProcessing, setUploadProcessing] = useState(false);
  const [uploadStep, setUploadStep] = useState<'waiting' | 'analyzing' | 'matching' | 'ready' | 'creating' | 'complete'>('waiting');
  const [mappingResults, setMappingResults] = useState<any[]>([]);
  const [showMappingResults, setShowMappingResults] = useState(false);
  
  
  // Thread assignment tracking
  const [creatorThreadStatus, setCreatorThreadStatus] = useState<Record<string, { hasThreads: boolean; checked: boolean }>>({});
  
  // Creator modal state
  const [creatorModalOpen, setCreatorModalOpen] = useState(false);
  const [editingCreator, setEditingCreator] = useState<Creator | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [creatorToDelete, setCreatorToDelete] = useState<Creator | null>(null);

  // Shopify status and selection state
  const [shopifyConnected, setShopifyConnected] = useState<boolean>(false);
  const [shopifyChannelId, setShopifyChannelId] = useState<string | null>(null);

  // Cell update confirmation via popover
  const [confirmOverwritePopover, setConfirmOverwritePopover] = useState<{
    rowId: string;
    columnId: string;
    currentValue: string;
    currentReasoning?: string;
    currentSource?: string;
    newValue: string;
  } | null>(null);

  // Helper function to handle workflow analysis queuing
  const handleWorkflowAnalysisQueuing = async (response: any, creatorId: string) => {
    if (response.workflow_analysis_queued && response.workflow_analysis_info) {
      const { table_id, project_id, current_status } = response.workflow_analysis_info;
      
      try {
        await workflowAnalysisQueue.enqueue(
          creatorId,
          table_id,
          project_id,
          current_status,
          'normal'
        );
        console.log(`✅ [WorkflowQueue] Queued analysis for ${creatorId}/${table_id}`);
      } catch (error) {
        console.error(`❌ [WorkflowQueue] Failed to queue analysis for ${creatorId}:`, error);
      }
    }
  };

  // Workflow analysis queue status
  const [queueStatus, setQueueStatus] = useState({
    isProcessing: false,
    queueLength: 0,
    currentRequest: null as any,
    completedCount: 0,
    errorCount: 0
  } as any);

  // Subscribe to queue status changes
  useEffect(() => {
    const unsubscribe = workflowAnalysisQueue.subscribe((status) => {
      setQueueStatus(status);
    });
    
    return unsubscribe;
  }, []);

  // Helper function to check if creator has assigned threads
  const checkCreatorThreads = async (creatorId: string): Promise<boolean> => {
    // Return cached result if already checked
    if (creatorThreadStatus[creatorId]?.checked) {
      return creatorThreadStatus[creatorId].hasThreads;
    }

    try {
      const projectId = currentTable?.project_id || currentTable?.project_info?.project_uuid;
      if (!projectId) {
        setCreatorThreadStatus(prev => ({
          ...prev,
          [creatorId]: { hasThreads: false, checked: true }
        }));
        return false;
      }

      const threadData = await outlookThreadsService.listCreatorThreads(creatorId, projectId);
      const hasThreads = threadData.assigned && threadData.assigned.length > 0;
      
      setCreatorThreadStatus(prev => ({
        ...prev,
        [creatorId]: { hasThreads, checked: true }
      }));
      
      return hasThreads;
    } catch (error) {
      console.error('❌ [ResearchTable] Error checking thread assignment:', error);
      setCreatorThreadStatus(prev => ({
        ...prev,
        [creatorId]: { hasThreads: false, checked: true }
      }));
      return false;
    }
  };

  // Helper function to check if a communication cell should be disabled
  const isCommunicationCellDisabled = (creatorId: string, column: TableColumn): boolean => {
    // All cells are now runnable - let AI handle communication availability gracefully
    return false;
  };

  // Helper function to check if a cell should be disabled due to platform mismatch or missing email
  const isPlatformCellDisabled = (creator: Creator, column: TableColumn): boolean => {
    // Check email availability AND connection for communication columns
    if (column.cell_type === 'communication') {
      // First check if user has connected their email account
      if (!hasOutlookConnected) {
        return true; // Disable if no email provider connected
      }
      
      // Then check if creator has a valid email
      const contactInfo = creator.contacts?.find(c => c.type === 'email')?.value || 
                         creator.creator_shipping?.email;
      
      if (!contactInfo || 
          contactInfo.includes('noemail_') || 
          contactInfo.includes('unknown.com') || 
          contactInfo.includes('@example.com')) {
        return true; // Disable cell if no valid email
      }
    }

    // Shopify cells require a connected shop
    if (column.cell_type === 'shopify') {
      if (!hasShopifyConnected) {
        return true; // Disable if Shopify not connected
      }
    }
    
    // Outreach columns require email connection
    if (column.cell_type === 'outreach') {
      if (!hasOutlookConnected) {
        return true; // Disable if no email provider connected
      }
      
      // Also check if creator has a valid email
      const contactInfo = creator.contacts?.find(c => c.type === 'email')?.value || 
                         creator.creator_shipping?.email;
      
      if (!contactInfo || 
          contactInfo.includes('noemail_') || 
          contactInfo.includes('unknown.com') || 
          contactInfo.includes('@example.com')) {
        return true; // Disable cell if no valid email
      }
    }

    // Check platform compatibility for platform-specific columns
    if (!column.platform_resources || column.platform_resources.length === 0) {
      return false;
    }
    
    const columnRequiresTikTok = column.platform_resources.some((r: any) => r.platform === 'tiktok');
    const columnRequiresInstagram = column.platform_resources.some((r: any) => r.platform === 'instagram');
    
    const creatorHasTikTok = creator.tiktok_data && creator.tiktok_data.length > 0;
    const creatorHasInstagram = creator.instagram_data && creator.instagram_data.length > 0;
    
    // If column requires TikTok but creator doesn't have TikTok data
    if (columnRequiresTikTok && !creatorHasTikTok && !columnRequiresInstagram) {
      return true;
    }
    
    // If column requires Instagram but creator doesn't have Instagram data
    if (columnRequiresInstagram && !creatorHasInstagram && !columnRequiresTikTok) {
      return true;
    }
    
    // If column requires both platforms but creator has neither
    if ((columnRequiresTikTok || columnRequiresInstagram) && !creatorHasTikTok && !creatorHasInstagram) {
      return true;
    }
    
    return false;
  };


  // Initialize and load table if tableId provided
  useEffect(() => {
    // Load Shopify connection status
    (async () => {
      try {
        const status = await shopifyStatusService.getStatus();
        setShopifyConnected(!!status?.isConnected);
        setShopifyChannelId(status?.channel_id || null);
      } catch (e) {
        setShopifyConnected(false);
        setShopifyChannelId(null);
      }
    })();

    if (tableId && !uploadMode) {
      loadExistingTable(tableId);
    } else if (uploadMode) {
      // Initialize empty table for upload mode - no predefined columns
      setColumns([]);
      setRows([]);
      setTableData({});
      // Create a mock table object for upload mode
      setCurrentTable({
        id: 'upload-temp',
        name: '',
        description: '',
        table_config: { columns: [] },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        column_count: 0,
        project_id: projectId
      });
      
      
    } else {
      setColumns([]);
      setRows([]);
      setTableData({});
    }
  }, [tableId, projectId, uploadMode]);

  // Check thread status for all creators when rows change
  useEffect(() => {
    const checkAllCreatorThreads = async () => {
      if (!rows.length || !currentTable) return;
      
      // Only check creators that haven't been checked yet
      const uncheckedCreators = rows.filter(row => 
        !creatorThreadStatus[row.creator.id]?.checked
      );
      
      if (uncheckedCreators.length === 0) return;
      
      console.log(`🔍 [ResearchTable] Checking thread status for ${uncheckedCreators.length} creators`);
      
      // Check thread status for all unchecked creators in parallel
      await Promise.all(
        uncheckedCreators.map(row => checkCreatorThreads(row.creator.id))
      );
    };

    checkAllCreatorThreads();
  }, [rows, currentTable]);

  // Helper function to save cell data to backend
  const saveCellData = async (rowId: string, columnId: string, cellData: CellData) => {
    if (currentTable) {
      try {
        // Convert CellData to CreatorCellData format for backend
        const creatorCellData = {
          value: cellData.value,
          loading: cellData.loading,
          error: cellData.error,
          reasoning: cellData.reasoning,
          source: cellData.source,
          confidence: cellData.confidence,
          timestamp: new Date().toISOString(),
          // Conversation-specific fields for email thread display
          messages: cellData.messages || [],
          highlight_text: cellData.highlight_text || '',
          source_message_id: cellData.source_message_id || '',
          context_found_in_message_id: cellData.context_found_in_message_id || '',
          // Pending change system
          pending_change: cellData.pending_change
        };
        
        await researchTableService.updateSingleCell(currentTable.id, rowId, columnId, creatorCellData);
        console.log('✅ [ResearchTable] Cell data saved:', rowId, columnId);
      } catch (error) {
        console.error('❌ [ResearchTable] Error saving cell data:', error);
      }
    }
  };

  const loadExistingTable = async (id: string) => {
    try {
      setLoading(true);
      
      // Clear existing data immediately to prevent showing old data
      setRows([]);
      setTableData({});
      setColumns([]);
      
      const table = await researchTableService.getResearchTable(id);
      setCurrentTable(table);
      
      // Initialize hidden columns from table config
      console.log('📥 Loading table, hidden_columns from backend:', table.table_config?.hidden_columns);
      if (table.table_config?.hidden_columns) {
        setHiddenColumns(new Set(table.table_config.hidden_columns));
        console.log('✅ Initialized hidden columns from backend:', table.table_config.hidden_columns);
      } else {
        console.log('⚠️ No hidden_columns in backend, initializing empty set');
        setHiddenColumns(new Set());
      }
      
      // Load columns from table config (include outreach columns)
      if (table.table_config.columns) {
        const loadedColumns = table.table_config.columns as TableColumn[];
        setColumns(loadedColumns);
      }
      
      // Load creators for this table
      await loadTableCreators(id);
      // Load table data
      await loadTableData(id);

    } catch (error) {
      console.error('Error loading table:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTableCreators = async (id: string) => {
    try {
      console.log('📚 [ResearchTable] Loading creators for table:', id);
      const response = await researchTableService.getTableCreators(id);
      
      // Convert creators to table rows
      const tableRows: TableRow[] = response.creators.map(creator => ({
        id: creator.id,
        creator,
        data: {}
      }));
      
      setRows(tableRows);
      console.log('✅ [ResearchTable] Loaded creators:', tableRows.length);
    } catch (error) {
      console.error('❌ [ResearchTable] Error loading creators:', error);
      // If no creators found, just set empty array (this is expected for new tables)
      setRows([]);
    }
  };

  // Add a single creator to the table without full refresh
  const handleCreatorAdded = async (creatorId: string) => {
    try {
      console.log('➕ [ResearchTable] Adding single creator to table:', creatorId);
      
      // Check if creator already exists in rows
      if (rows.some(r => r.id === creatorId)) {
        console.log('ℹ️ [ResearchTable] Creator already in table, skipping');
        return;
      }
      
      // Fetch just this creator's data
      const creator = await creatorService.getCreator(creatorId);
      
      // Create empty cell data for this creator
      const newCreatorCells: Record<string, CellData> = {};
      columns.forEach(col => {
        newCreatorCells[col.id] = {
          value: '',
          loading: false
        };
      });
      
      // Create new row
      const newRow: TableRow = {
        id: creator.id,
        creator,
        data: {}
      };
      
      // Append to rows
      setRows(prev => [...prev, newRow]);
      
      // Add empty cells for this creator
      setTableData(prev => ({
        ...prev,
        [creator.id]: newCreatorCells
      }));
      
      console.log('✅ [ResearchTable] Successfully added creator to table');
    } catch (error) {
      console.error('❌ [ResearchTable] Error adding creator:', error);
    }
  };

  const loadTableData = async (id: string) => {
    try {
      console.log('📊 [ResearchTable] Loading table data for:', id);
      const response = await researchTableService.getTableData(id);
      // Convert CreatorCellData to CellData format
      const convertedTableData: Record<string, Record<string, CellData>> = {};
      const rawTableData = response.tableData || {};
      
      const cellsToCleanup: Array<{ creatorId: string; columnId: string; cellData: CellData }> = [];
      
      Object.keys(rawTableData).forEach(creatorId => {
        convertedTableData[creatorId] = {};
        Object.keys(rawTableData[creatorId]).forEach(columnId => {
          const cellData = rawTableData[creatorId][columnId];
          
          // Check if pending_change value is identical to current value (cleanup stale pending changes)
          let cleanedPendingChange = cellData.pending_change;
          let needsCleanup = false;
          if (cellData.pending_change && cellData.value) {
            // Use aggressive normalization for comparison
            const normalizeValue = (val: string) => {
              return val
                .trim()
                .toLowerCase()
                .replace(/\s+/g, ' ')
                .replace(/[\r\n]+/g, '')
                .replace(/[^\w\s]/g, '');
            };
            
            const currentValueNormalized = normalizeValue(cellData.value);
            const pendingValueNormalized = normalizeValue(cellData.pending_change.value);
            if (currentValueNormalized === pendingValueNormalized) {
              console.log(`🧹 [ResearchTable] Cleaning stale pending_change for ${creatorId}/${columnId} (values identical)`);
              console.log(`  - Current: "${cellData.value}"`);
              console.log(`  - Pending: "${cellData.pending_change.value}"`);
              cleanedPendingChange = undefined;
              needsCleanup = true;
            }
          }
          
          const convertedCell: CellData = {
            value: cellData.value || '',
            loading: cellData.loading || false,
            error: cellData.error || undefined,
            source: cellData.source,
            reasoning: cellData.reasoning,
            confidence: cellData.confidence,
            fetch_time: cellData.fetch_time,
            timestamp: cellData.timestamp,
            updated_at: cellData.updated_at,
            // Conversation-specific fields for email thread display
            messages: cellData.messages || [],
            highlight_text: cellData.highlight_text || '',
            source_message_id: cellData.source_message_id || '',
            context_found_in_message_id: cellData.context_found_in_message_id || '',
            // Pending change system (with stale pending changes cleaned)
            pending_change: cleanedPendingChange
          };
          
          convertedTableData[creatorId][columnId] = convertedCell;
          
          // Track cells that need cleanup in backend
          if (needsCleanup) {
            cellsToCleanup.push({ creatorId, columnId, cellData: convertedCell });
          }
        });
      });
      
      setTableData(convertedTableData);
      console.log('✅ [ResearchTable] Loaded table data:', Object.keys(response.tableData || {}).length, 'creators');
      
      // Persist cleaned cells to backend (remove stale pending changes from database)
      if (cellsToCleanup.length > 0) {
        console.log(`🧹 [ResearchTable] Persisting cleanup of ${cellsToCleanup.length} stale pending changes to backend`);
        for (const { creatorId, columnId, cellData } of cellsToCleanup) {
          await saveCellData(creatorId, columnId, cellData);
        }
      }
    } catch (error) {
      console.error('❌ [ResearchTable] Error loading table data:', error);
      // If no data found, just set empty object (this is expected for new tables)
      setTableData({});
    }
  };





  const handleAddColumn = () => {
    // Don't need to do anything here - ResearchTableView will handle it with popover
  };

  const handleReorderColumns = async (newColumns: TableColumn[]) => {
    setColumns(newColumns);
    await saveTableConfig(newColumns);
  };

  const handleEditColumn = (column: TableColumn) => {
    // Don't need to do anything here - ResearchTableView will handle it with popover
  };

  const handleDeleteColumn = async (columnId: string) => {
    const newColumns = columns.filter(col => col.id !== columnId);
    setColumns(newColumns);
    
    // Clean up table data
    const newTableData = { ...tableData };
    Object.keys(newTableData).forEach(rowId => {
      delete newTableData[rowId][columnId];
    });
    setTableData(newTableData);
    
    // Auto-save table configuration
    await saveTableConfig(newColumns);
  };

  const handleSaveColumn = async (column: TableColumn) => {
    let newColumns: TableColumn[];
    let shouldRecalculate = false;
    
    // Check if column already exists (editing) or is new (adding)
    const existingColumn = columns.find(col => col.id === column.id);
    const isEditing = !!existingColumn;
    
    if (isEditing) {
      // Check if this is a relationship column with a changed formula
      if (existingColumn && 
          column.cell_type === 'relationship' && 
          existingColumn.cell_type === 'relationship' &&
          existingColumn.relationship_config?.prompt !== column.relationship_config?.prompt) {
        shouldRecalculate = true;
        console.log('🔄 Formula changed - will recalculate all cells');
      }
      
      // Update existing column
      newColumns = columns.map(col => col.id === column.id ? column : col);
      setColumns(newColumns);
    } else {
      // Add new column
      newColumns = [...columns, column];
      setColumns(newColumns);
    }
    
    // Auto-save table configuration
    await saveTableConfig(newColumns);
    
    // Recalculate all cells in this column if formula changed
    if (shouldRecalculate) {
      console.log('🔄 Recalculating all cells for column:', column.name);
      for (const row of rows) {
        if (!row.id.startsWith('skeleton-') && !row.id.startsWith('mapping-')) {
          try {
            await handleResearchCell(row.id, column.id);
          } catch (error) {
            console.error(`Failed to recalculate ${row.id}:`, error);
          }
        }
      }
    }
    
    // Auto-research for new platform columns with auto_update enabled
    if (!isEditing && column.auto_update && column.cell_type === 'normal') {
      console.log('✨ [AUTO-UPDATE] New auto-update column added - researching all existing creators');
      
      // SAFEGUARD: Limit to reasonable number of creators
      const MAX_AUTO_RESEARCH_CELLS = 50;
      const validRows = rows.filter(row => !row.id.startsWith('skeleton-') && !row.id.startsWith('mapping-'));
      
      if (validRows.length > MAX_AUTO_RESEARCH_CELLS) {
        message.warning(`Auto-research limited to ${MAX_AUTO_RESEARCH_CELLS} creators (you have ${validRows.length})`);
        console.warn(`⚠️ [AUTO-UPDATE] Too many creators (${validRows.length}), limiting to ${MAX_AUTO_RESEARCH_CELLS}`);
      } else {
        message.info(`Auto-researching "${column.name}" for ${validRows.length} creators...`);
      }
      
      // Research this column for existing creators (up to limit)
      const rowsToResearch = validRows.slice(0, MAX_AUTO_RESEARCH_CELLS);
      for (const row of rowsToResearch) {
        try {
          // Use a small delay to avoid overwhelming the API
          setTimeout(() => {
            handleResearchCell(row.id, column.id);
          }, 100 * rowsToResearch.indexOf(row));
        } catch (error) {
          console.error(`Failed to auto-research ${row.id}:`, error);
        }
      }
    }
  };


  const saveHiddenColumnsToBackend = async (hiddenColumnsSet: Set<string>) => {
    if (!tableId || !currentTable) {
      console.error('❌ Cannot save hidden columns: missing tableId or currentTable');
      return;
    }
    
    try {
      console.log('💾 Saving hidden columns to backend:', Array.from(hiddenColumnsSet));
      console.log('📊 Current table config:', currentTable.table_config);
      
      const updatedConfig = {
        ...currentTable.table_config,
        hidden_columns: Array.from(hiddenColumnsSet)
      };
      
      console.log('📤 Sending updated config:', updatedConfig);
      
      await researchTableService.updateResearchTable(tableId, {
        table_config: updatedConfig
      });
      
      console.log('✅ Saved hidden columns to backend:', Array.from(hiddenColumnsSet));
    } catch (error) {
      console.error('❌ Error saving hidden columns:', error);
      message.error('Failed to save column visibility');
    }
  };

  const saveTableConfig = async (columnsToSave: TableColumn[]) => {
    if (!currentTable) return;
    
    console.log(`💾 [ResearchTable] Auto-saving table config for table: ${currentTable.id}`);
    console.log(`📋 [ResearchTable] Saving ${columnsToSave.length} columns:`, columnsToSave.map(c => c.name));
    
    try {
      const updatedConfig = {
        ...currentTable.table_config,
        columns: columnsToSave
      };
      
      const updatedTable = await researchTableService.saveTableConfig(currentTable.id, updatedConfig as ResearchTableConfig);
      console.log(`✅ [ResearchTable] Table config saved successfully`);
      
      // Refresh currentTable to ensure it has the latest column data
      // This is critical for validation when running research on newly added columns
      if (updatedTable) {
        setCurrentTable(updatedTable);
      }
    } catch (error) {
      console.error('❌ [ResearchTable] Error saving table config:', error);
      // Don't show error message to user for auto-save
    }
  };

  // Helper function to check if creator has contact methods
  const hasContactMethod = (creator: Creator): boolean => {
    // Check contacts array for email
    if (creator.contacts && Array.isArray(creator.contacts)) {
      const hasEmail = creator.contacts.some(contact => contact.type === 'email' && contact.value);
      if (hasEmail) return true;
    }
    
    // Fallback: check creator_shipping for email
    if (creator.creator_shipping?.email) return true;
    
    return false;
  };

  // Helper function to get row data for a specific creator
  const getCreatorRowData = (creator: Creator | null): Record<string, CellData> => {
    if (!creator) return {};
    
    // Find the row that matches this creator
    const matchingRow = rows.find(r => r.creator.id === creator.id);
    if (!matchingRow) {
      console.warn(`[ResearchTable] No row found for creator ${creator.id}`);
      return {};
    }
    
    // Return the table data for this row, with fallback to empty object
    return tableData[matchingRow.id] || {};
  };

  const handleOpenCreatorConversation = (creator: Creator, outreachColumn?: TableColumn) => {
    // Only open if creator has contact methods
    if (!hasContactMethod(creator)) {
      console.log('Cannot open conversation - creator has no contact information');
      return;
    }
    
    setSelectedCreator(creator);
    setSelectedOutreachColumn(outreachColumn || null);
    setOutreachSidebarOpen(true);
  };

  const handleCloseOutreachSidebar = () => {
    setOutreachSidebarOpen(false);
    setSelectedCreator(null);
    setSelectedOutreachColumn(null);
  };


  const handleOpenCellSidebar = (cellData: CellData, column: TableColumn, creator?: Creator) => {
    setSelectedCell({ data: cellData, column, creator });
    setCellSidebarOpen(true);
  };

  const handleCloseCellSidebar = () => {
    setCellSidebarOpen(false);
    setSelectedCell(null);
  };

  const handleUpdateCellValue = async (rowId: string, columnId: string, newValue: string) => {
    try {
      console.log('💬 [ResearchTable] Manually updating cell:', rowId, columnId, newValue);
      
      // Find the column to get its data type
      const column = columns.find(col => col.id === columnId);
      const dataType = column?.data_type;
      
      // Validate the value
      const validation = validateCellValue(newValue, dataType);
      if (!validation.isValid) {
        console.error('❌ [ResearchTable] Invalid cell value:', validation.errorMessage);
        // Show error in cell temporarily
        setTableData(prev => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            [columnId]: {
              ...prev[rowId]?.[columnId],
              value: newValue,
              error: validation.errorMessage
            }
          }
        }));
        return;
      }
      
      // Format the value based on data type
      const formattedValue = formatCellValue(newValue, dataType);
      
      // Check if cell already has a value - if so, show confirmation modal
      const currentCell = tableData[rowId]?.[columnId];
      const hasExistingValue = currentCell?.value && currentCell.value.trim() !== '';
      
      if (hasExistingValue) {
        // Check if the new value is the same as existing (aggressive normalization)
        const normalizeValue = (val: string) => {
          return val
            .trim()
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .replace(/[\r\n]+/g, '')
            .replace(/[^\w\s]/g, '');
        };
        
        const existingValueNormalized = normalizeValue(currentCell.value);
        const newValueNormalized = normalizeValue(formattedValue);
        
        if (existingValueNormalized === newValueNormalized) {
          console.log('✅ [ResearchTable] Manual value is identical to existing - keeping AI data (reasoning/sources)');
          console.log(`  - Existing: "${currentCell.value}"`);
          console.log(`  - New: "${formattedValue}"`);
          console.log(`  - Normalized existing: "${existingValueNormalized}"`);
          console.log(`  - Normalized new: "${newValueNormalized}"`);
          // Just keep the existing cell data unchanged (preserves AI reasoning/sources)
          return;
        }
        
        // Values are different - show confirmation popover for overwriting existing value
        setConfirmOverwritePopover({
          rowId,
          columnId,
          currentValue: currentCell.value,
          currentReasoning: currentCell.reasoning,
          currentSource: currentCell.source,
          newValue: formattedValue
        });
        return; // Don't save yet - wait for user confirmation
      }
      
      // Cell is empty - save directly
      const cellData: CellData = {
        value: formattedValue,
        loading: false,
        error: undefined, // Clear any previous errors
        // Keep existing metadata if available
        source: tableData[rowId]?.[columnId]?.source,
        reasoning: tableData[rowId]?.[columnId]?.reasoning,
        confidence: tableData[rowId]?.[columnId]?.confidence
      };

      // Update local state
      setTableData(prev => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          [columnId]: cellData
        }
      }));

      // Persist to backend
      await saveCellData(rowId, columnId, cellData);
      
      console.log('✅ [ResearchTable] Manual cell update saved with formatted value:', formattedValue);
    } catch (error) {
      console.error('❌ [ResearchTable] Error updating cell manually:', error);
    }
  };

  // Handler for confirming manual overwrite of existing value
  const handleConfirmOverwrite = async () => {
    if (!confirmOverwritePopover) return;

    const { rowId, columnId, newValue } = confirmOverwritePopover;
    
    const cellData: CellData = {
      value: newValue,
      loading: false,
      error: undefined,
    };

    // Update local state
    setTableData(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [columnId]: cellData
      }
    }));

    // Persist to backend
    await saveCellData(rowId, columnId, cellData);
    
    // Close popover
    setConfirmOverwritePopover(null);
    console.log('✅ [ResearchTable] Manual overwrite confirmed and saved');
  };

  // Pending change handlers
  const handleAcceptPendingChange = async (rowId: string, columnId: string) => {
    if (!tableId) return;
    
    try {
      console.log('🔄 [ResearchTable] Accepting pending change:', rowId, columnId);
      
      // Call the service to accept the pending change
      const result = await researchTableService.acceptPendingChange(tableId, rowId, columnId);
      
      // Convert CreatorCellData to CellData format
      const cellData: CellData = {
        value: result.cellData.value || '',
        loading: result.cellData.loading || false,
        error: result.cellData.error || undefined,
        source: result.cellData.source,
        reasoning: result.cellData.reasoning,
        confidence: result.cellData.confidence,
        timestamp: result.cellData.timestamp,
        updated_at: result.cellData.updated_at,
        messages: result.cellData.messages,
        highlight_text: result.cellData.highlight_text,
        source_message_id: result.cellData.source_message_id,
        pending_change: result.cellData.pending_change
      };
      
      // Update local state with the accepted change
      setTableData(prev => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          [columnId]: cellData
        }
      }));
      
      console.log('✅ [ResearchTable] Pending change accepted successfully');
      message.success('Change accepted');
    } catch (error) {
      console.error('❌ [ResearchTable] Failed to accept pending change:', error);
      message.error('Failed to accept change');
    }
  };

  const handleDismissPendingChange = async (rowId: string, columnId: string) => {
    if (!tableId) return;
    
    try {
      console.log('❌ [ResearchTable] Dismissing pending change:', rowId, columnId);
      
      // Call the service to dismiss the pending change
      const result = await researchTableService.dismissPendingChange(tableId, rowId, columnId);
      
      // Convert CreatorCellData to CellData format
      const cellData: CellData = {
        value: result.cellData.value || '',
        loading: result.cellData.loading || false,
        error: result.cellData.error || undefined,
        source: result.cellData.source,
        reasoning: result.cellData.reasoning,
        confidence: result.cellData.confidence,
        timestamp: result.cellData.timestamp,
        updated_at: result.cellData.updated_at,
        messages: result.cellData.messages,
        highlight_text: result.cellData.highlight_text,
        source_message_id: result.cellData.source_message_id,
        pending_change: result.cellData.pending_change
      };
      
      // Update local state with the dismissed change
      setTableData(prev => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          [columnId]: cellData
        }
      }));
      
      console.log('✅ [ResearchTable] Pending change dismissed successfully');
      message.success('Change dismissed');
    } catch (error) {
      console.error('❌ [ResearchTable] Failed to dismiss pending change:', error);
      message.error('Failed to dismiss change');
    }
  };

  const handleExportCSV = () => {
    try {
      console.log('📄 [ResearchTable] Starting CSV download...');
      
      if (rows.length === 0) {
        console.warn('No data to export');
        return;
      }
      
      const tableName = currentTable?.name || 'Research Table';
      const filename = generateExportFilename(tableName);
      
      // Export with creator details but no metadata, excluding hidden columns
      const csvContent = exportTableToCSV(
        columns,
        rows,
        tableData,
        tableName,
        {
          includeCreatorDetails: true,
          includeMetadata: false,
          includeEmptyCells: true,
          hiddenColumns: hiddenColumns
        }
      );
      
      downloadCSV(csvContent, filename);
      
      console.log('✅ [ResearchTable] CSV download completed:', filename);
    } catch (error) {
      console.error('❌ [ResearchTable] CSV download failed:', error);
    }
  };

  // Handle export trigger from parent component
  useEffect(() => {
    if (exportTrigger > 0) {
      handleExportCSV();
    }
  }, [exportTrigger]);

  // Handle copy trigger from parent component
  useEffect(() => {
    if (copyTrigger > 0) {
      handleCopyToClipboard();
    }
  }, [copyTrigger]);

  // Handle refresh trigger from parent component
  useEffect(() => {
    if (refreshTrigger > 0) {
      // Reload the table data
      if (tableId) {
        loadExistingTable(tableId);
      }
    }
  }, [refreshTrigger, tableId]);

  // Handle run all trigger from parent component
  useEffect(() => {
    if (runAllTrigger > 0) {
      handleResearchAllRows();
    }
  }, [runAllTrigger]);

  // Handle run unrun trigger from parent component
  useEffect(() => {
    if (runUnrunTrigger > 0) {
      handleResearchUnrunRows();
    }
  }, [runUnrunTrigger]);

  // Handle analyze outreach trigger from parent component
  useEffect(() => {
    if (analyzeOutreachTrigger && analyzeOutreachTrigger.timestamp > 0) {
      handleAnalyzeOutreachTemplate(analyzeOutreachTrigger.templateId);
    }
  }, [analyzeOutreachTrigger]);

  // Load table data when table changes
  useEffect(() => {
    if (currentTable && currentTable.table_config.columns) {
      // Show table-specific columns
      const loadedColumns = currentTable.table_config.columns as TableColumn[];
      setColumns(loadedColumns);
    }
  }, [currentTable]);

  // Listen for creator added to project event
  useEffect(() => {
    const handleCreatorAdded = (event: any) => {
      const creator = event.detail?.creator;
      if (!creator) return;
      
      // Check if creator is already in the table
      const creatorExists = rows.some(row => row.id === creator.id);
      if (creatorExists) return;
      
      console.log('✨ [ResearchTable] Adding new creator to table:', creator.id);
      
      // Create new row for the creator
      const newRow: TableRow = {
        id: creator.id,
        creator,
        data: {}
      };
      
      // Append to rows
      setRows(prev => [...prev, newRow]);
    };

    window.addEventListener('creatorAddedToProject', handleCreatorAdded);
    
    return () => {
      window.removeEventListener('creatorAddedToProject', handleCreatorAdded);
    };
  }, [rows]); // Include rows in deps to check if creator exists

  const handleAnalyzeOutreachTemplate = async (templateId: string) => {
    console.log('🔍 [ResearchTable] Analyzing outreach template:', templateId, 'for', rows.length, 'creators');
    
    if (rows.length === 0) {
      console.warn('No creators to analyze');
      return;
    }

    // This would run the outreach analysis similar to how outreach columns worked
    // For now, just log what we would do
    console.log('🎯 [ResearchTable] Would analyze template:', templateId, 'for creators:', rows.map(r => r.creator.id));
    
    // TODO: Implement actual outreach analysis logic here
    // This should work similarly to how handleResearchCell worked for outreach columns
  };

  const handleCopyToClipboard = async () => {
    console.log('📋 [ResearchTable] Starting copy to clipboard...');
    
    if (rows.length === 0) {
      console.warn('No data to copy');
      throw new Error('No data to copy');
    }
    
    const tableName = currentTable?.name || 'Research Table';
    
    // Export with creator details but no metadata, excluding hidden columns
    const csvContent = exportTableToCSV(
      columns,
      rows,
      tableData,
      tableName,
      {
        includeCreatorDetails: true,
        includeMetadata: false,
        includeEmptyCells: true,
        hiddenColumns: hiddenColumns
      }
    );
    
    const success = await copyToClipboard(csvContent);
    
    if (success) {
      console.log('✅ [ResearchTable] Data copied to clipboard');
    } else {
      console.error('❌ [ResearchTable] Failed to copy to clipboard');
      throw new Error('Failed to copy to clipboard');
    }
  };



  const handleResearchCell = async (rowId: string, columnId: string, force: boolean = false) => {
    console.log('🔬 [handleResearchCell] tableId prop value:', tableId, 'type:', typeof tableId);
    
    // IMPORTANT: Capture existing cell value at the START before any async operations
    // This prevents race conditions when running multiple cells in parallel
    const existingCellSnapshot = tableData[rowId]?.[columnId];
    const hasExistingValueSnapshot = existingCellSnapshot?.value && existingCellSnapshot.value.trim() !== '';
    
    console.log(`📸 [handleResearchCell] Snapshot taken for ${rowId}/${columnId}:`, {
      hasValue: hasExistingValueSnapshot,
      value: existingCellSnapshot?.value?.substring(0, 50)
    });
    
    const row = rows.find(r => r.id === rowId);
    const column = columns.find(c => c.id === columnId);
    
    if (!row || !column) return;
    
    // Only allow research for normal, communication, outreach, relationship, shopify, and track_posts columns
    if (column.cell_type !== 'normal' && column.cell_type !== 'communication' && column.cell_type !== 'outreach' && column.cell_type !== 'relationship' && column.cell_type !== 'shopify' && column.cell_type !== 'track_posts') {
      console.log('Research skipped - not a researchable column');
      return;
    }

    // Check platform compatibility for platform-specific columns
    if (column.platform_resources && column.platform_resources.length > 0) {
      const columnRequiresTikTok = column.platform_resources.some((r: any) => r.platform === 'tiktok');
      const columnRequiresInstagram = column.platform_resources.some((r: any) => r.platform === 'instagram');
      
      const creatorHasTikTok = row.creator.tiktok_data && row.creator.tiktok_data.length > 0;
      const creatorHasInstagram = row.creator.instagram_data && row.creator.instagram_data.length > 0;
      
      // If column requires TikTok but creator doesn't have TikTok data
      if (columnRequiresTikTok && !creatorHasTikTok && !columnRequiresInstagram) {
        message.warning(`Cannot research ${column.name} - creator doesn't have TikTok data connected`);
        console.log('Research skipped - column requires TikTok but creator has no TikTok data');
        return;
      }
      
      // If column requires Instagram but creator doesn't have Instagram data
      if (columnRequiresInstagram && !creatorHasInstagram && !columnRequiresTikTok) {
        message.warning(`Cannot research ${column.name} - creator doesn't have Instagram data connected`);
        console.log('Research skipped - column requires Instagram but creator has no Instagram data');
        return;
      }
      
      // If column requires both platforms but creator has neither
      if ((columnRequiresTikTok || columnRequiresInstagram) && !creatorHasTikTok && !creatorHasInstagram) {
        message.warning(`Cannot research ${column.name} - creator doesn't have any platform data connected`);
        console.log('Research skipped - column requires platform data but creator has none');
        return;
      }
    }

    // Shopify column actions
    // Handle Shopify columns
    if (column.cell_type === 'shopify') {
      if (!shopifyConnected || !shopifyChannelId) {
        message.error('Connect a Shopify store first in Settings.');
        return;
      }

      // Route based on Shopify subtype
      if (column.shopify_config?.subtype === 'discount_code') {
        // Get price rule from column config
        const priceRuleId = column.shopify_config.price_rule_id;
        const priceRuleName = column.shopify_config.price_rule_name;

        if (!priceRuleId) {
          message.warning('Please configure a price rule for this column in the column settings.');
          return;
        }

        try {
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [columnId]: { ...(prev[rowId]?.[columnId] || {}), loading: true }
            }
          }));

          // Generate code name using template
          const codeTemplate = column.shopify_config.code_name_template || 'CREATOR{handle}';
          const generatedCode = generateCodeName(codeTemplate, row.creator);

          const result = await shopifyActionsService.createCreatorCode({
            creator_id: row.creator.id,
            price_rule_id: priceRuleId,
            code: generatedCode,
            channel_id: shopifyChannelId,
            project_uuid: currentTable?.project_id || currentTable?.project_info?.project_uuid,
          });

          const finalCode = result?.shopify_discount_code || generatedCode;
          
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [columnId]: { value: finalCode, loading: false }
            }
          }));

          // Persist to research_table_data
          const dcCell: CellData = {
            value: finalCode,
            loading: false,
            timestamp: new Date().toISOString(),
            meta: {
              shopify_price_rule_id: priceRuleId,
              shopify_price_rule_name: priceRuleName,
              shopify_channel_id: shopifyChannelId,
              shopify_discount_code: finalCode
            }
          };
          await saveCellData(rowId, columnId, dcCell);
          message.success(`Discount code generated: ${finalCode}`);
        } catch (e: any) {
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [columnId]: { value: '', loading: false, error: e?.message || 'Failed to create discount code' }
            }
          }));
          message.error(e?.message || 'Failed to create discount code');
        }
        return;

      } else if (column.shopify_config?.subtype === 'analytics') {
        // Handle analytics query
        const prompt = column.shopify_config.prompt || column.prompt;
        if (!prompt) {
          message.warning('No analytics query configured for this column.');
          return;
        }

        // Find discount code for this creator
        const discountCodeColumn = columns.find(col => 
          col.cell_type === 'shopify' && col.shopify_config?.subtype === 'discount_code'
        );
        
        const discountCode = discountCodeColumn 
          ? tableData[rowId]?.[discountCodeColumn.id]?.value || tableData[rowId]?.[discountCodeColumn.id]?.meta?.shopify_discount_code
          : null;

        if (!discountCode) {
          message.warning('Generate a discount code first before fetching analytics.');
          return;
        }

        try {
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [columnId]: { ...(prev[rowId]?.[columnId] || {}), loading: true }
            }
          }));

          const { shopifyAnalyticsService } = await import('../../services/shopifyAnalyticsService');
          const analyticsResult = await shopifyAnalyticsService.getDiscountCodeAnalytics({
            code: discountCode,
            channel_id: shopifyChannelId,
            prompt: prompt
          });

          const resultValue = analyticsResult.result || analyticsResult.interpreted_metric || 'No data';

          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [columnId]: { value: resultValue, loading: false }
            }
          }));

          const analyticsCell: CellData = {
            value: resultValue,
            loading: false,
            timestamp: new Date().toISOString(),
            meta: {
              discount_code: discountCode,
              prompt: prompt,
              full_analytics: analyticsResult
            }
          };
          await saveCellData(rowId, columnId, analyticsCell);
        } catch (e: any) {
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [columnId]: { value: '', loading: false, error: e?.message || 'Failed to fetch analytics' }
            }
          }));
          message.error(e?.message || 'Failed to fetch analytics');
        }
        return;
      }
    }

    // Check if communication column requires a thread (needs email to lookup conversations)
    if (column.cell_type === 'communication' && column.requires_thread) {
      const hasEmail = row.creator.contacts?.some(c => c.type === 'email') || 
                       row.creator.creator_shipping?.email;
      if (!hasEmail) {
        message.warning(`Cannot research ${column.name} - creator doesn't have an email address to find conversations`);
        console.log('Research skipped - column requires thread but creator has no email');
        return;
      }
    }

    // Check column dependencies
    if (column.depends_on) {
      const dependencyColumn = columns.find(c => c.id === column.depends_on!.column_id);
      const dependencyValue = tableData[rowId]?.[column.depends_on.column_id]?.value;
      
      if (!dependencyValue || !column.depends_on.required_values.includes(dependencyValue)) {
        const dependencyColumnName = dependencyColumn?.name || column.depends_on.column_id;
        const requiredValuesText = column.depends_on.required_values.join(' or ');
        message.warning(`Cannot research ${column.name} until ${dependencyColumnName} is ${requiredValuesText}`);
        console.log(`Research skipped - dependency not met: ${column.depends_on.column_id} must be ${requiredValuesText}, but is: ${dependencyValue}`);
        return;
      }
    }
    
    // For conversation-based columns, check if creator has valid email
    if (column.cell_type === 'communication') {
      const contactInfo = row.creator.contacts?.find(c => c.type === 'email')?.value || 
                         row.creator.creator_shipping?.email;
      
      if (!contactInfo || 
          contactInfo.includes('noemail_') || 
          contactInfo.includes('unknown.com') || 
          contactInfo.includes('@example.com')) {
        message.warning(`Cannot research ${column.name} - creator doesn't have a valid email address`);
        console.log('Research skipped - no valid email for conversation analysis');
        return;
      }
    }
    
    // Note: Removed thread assignment checking - let AI handle communication availability gracefully

    // VALIDATION: Ensure column exists in table config (fixes issue with new columns on predefined template tables)
    if (!currentTable) {
      console.error('❌ [ResearchTable] Cannot research - no current table');
      message.error('Table not loaded. Please refresh the page.');
      return;
    }
    
    const columnInConfig = (currentTable.table_config?.columns as TableColumn[] | undefined)?.find((c) => c.id === columnId);
    if (!columnInConfig) {
      console.error('❌ [ResearchTable] Column not found in table config:', {
        columnId,
        columnName: column.name,
        tableId: currentTable.id
      });
      message.warning(`Column "${column.name}" not found in table configuration. Please save the column first.`);
      return;
    }

    // Set loading state
    setTableData(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [columnId]: { ...prev[rowId]?.[columnId], loading: true, error: undefined }
      }
    }));

    try {
      // HANDLE RELATIONSHIP COLUMNS (AI CALCULATION)
      if (column.cell_type === 'relationship' && column.relationship_config) {
        console.log('🧮 [CALCULATION] Starting AI calculation for:', column.name);
        const config = column.relationship_config;
        const formula = config.prompt;
        
        // Get ALL available column data (let AI figure out which ones to use)
        const existingData = tableData[rowId] || {};
        const sourceColumnsData: Record<string, { name: string; value: string }> = {};
        
        // Include all columns except relationship columns
        for (const col of columns) {
          if (col.id !== columnId && col.cell_type !== 'relationship') {
            const colData = existingData[col.id];
            sourceColumnsData[col.id] = {
              name: col.name,
              value: colData?.value || ''
            };
          }
        }
        
        console.log('🧮 [CALCULATION] Available columns for AI:', Object.keys(sourceColumnsData).length);
        
        // Build request payload for relationship AI agent
        const requestPayload: any = {
          prompt: formula,
          source_columns: sourceColumnsData,
          result_format: config.result_format || 'number',
          currency: (config as any).currency || 'USD',
          context: '',
          column_type: 'relationship',
          relationship_config: config
        };
        
        console.log('📤 [CALCULATION] Sending to relationship AI agent:', requestPayload);
        
        // Call the AI agent
        const response = await researchAIService.researchCreator(requestPayload);
        
        if (!response.success) {
          throw new Error(response.error || 'Calculation failed');
        }
        
        const formattedResult = response.result || '';
        
        const cellData: CellData = {
          value: formattedResult,
          loading: false,
          reasoning: response.reasoning || 'AI calculation performed',
          timestamp: new Date().toISOString()
        };
        
        // Update cell with calculated value
        setTableData(prev => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            [columnId]: cellData
          }
        }));
        
        // Save to backend
        await saveCellData(rowId, columnId, cellData);
        
        console.log('✅ [CALCULATION] AI calculation complete:', formattedResult);
        
        return; // Exit early for relationship columns
      }
      
      // Use the specialized research AI agent
      const platformData = getCreatorPrimaryPlatform(row.creator);
      
      // Check if creator has multiple platforms
      const platforms = [];
      const handles: Record<string, string> = {};
      
      if (row.creator.tiktok_data && row.creator.tiktok_data.length > 0) {
        platforms.push('tiktok');
        handles.tiktok = row.creator.tiktok_data[0].handle;
      }
      if (row.creator.instagram_data && row.creator.instagram_data.length > 0) {
        platforms.push('instagram');
        handles.instagram = row.creator.instagram_data[0].handle;
      }
      if (row.creator.twitch_data && row.creator.twitch_data.length > 0) {
        platforms.push('twitch');
        handles.twitch = row.creator.twitch_data[0].handle;
      }
      
      const creatorInfo: CreatorInfo = platforms.length > 1 ? {
        // Multi-platform creator
        id: row.creator.id, // Add creator ID
        name: platformData.name,
        handle: platformData.handle, // Primary handle for display
        platform: 'multi',
        followers_count: platformData.followers_count,
        engagement_rate: platformData.engagement_rate,
        handles: handles // Include all platform handles
      } : {
        // Single platform creator
        id: row.creator.id, // Add creator ID
        name: platformData.name,
        handle: platformData.handle,
        platform: platformData.platform,
        followers_count: platformData.followers_count,
        engagement_rate: platformData.engagement_rate
      };
      
      // Build context from previous columns
      const existingData = tableData[rowId] || {};
      const previousResearch = columns
        .filter(col => col.id !== columnId && existingData[col.id]?.value && !existingData[col.id]?.error)
        .map(col => `${col.name}: ${existingData[col.id].value}`)
        .join('\n');
      
      const contextWithPreviousData = `Creator research for @${platformData.handle} on ${platformData.platform}` + 
        (previousResearch ? `\n\nPreviously researched data for this creator:\n${previousResearch}` : '');
      


              // Debug logging for labels
              if (column.data_type === 'labels') {
                console.log('🏷️ [DEBUG] LABELS RESEARCH REQUEST:', {
                  data_type: column.data_type,
                  label_options: column.label_options,
                  prompt: column.prompt,
                  column: column
                });
              }

              // Only include project_id if it actually exists and is not empty
              const projectId = currentTable?.project_id || currentTable?.project_info?.project_uuid;
              // Determine resources based on column type
              const resources = column.cell_type === 'communication' && column.communication_sources 
                ? column.communication_sources 
                : getDefaultResearchSources(platformData.platform, row.creator, column);
              
              let enhancedContext = contextWithPreviousData;
              let effectivePrompt = column.prompt || '';
              let effectiveLabels = column.label_options || [];

              // Build request payload based on column type
              const requestPayload: any = {
                prompt: effectivePrompt,
                creator_info: creatorInfo,
                resources: resources,
                context: enhancedContext,
                response_format: column.response_format,
                data_type: column.data_type,
                label_options: effectiveLabels
              };

              // Add column-type-specific fields
              if (column.cell_type === 'normal') {
                // PLATFORM COLUMN - only include platform resources
                requestPayload.platform_resources = column.platform_resources || [];
                
                // Add target platform to respect column's platform configuration
                // Try platform_icon first (new columns), then extract from platform_resources (old columns)
                console.log('🎯 [TARGET_PLATFORM] Column platform_icon:', column.platform_icon);
                console.log('🎯 [TARGET_PLATFORM] Column platform_resources:', column.platform_resources);
                
                let targetPlatform = column.platform_icon;
                
                // FALLBACK: Extract platform from platform_resources if platform_icon not set
                if (!targetPlatform && column.platform_resources && column.platform_resources.length > 0) {
                  const firstResource = column.platform_resources[0];
                  if (firstResource.platform) {
                    targetPlatform = firstResource.platform;
                    console.log('🔄 [TARGET_PLATFORM] Extracted from platform_resources:', targetPlatform);
                  }
                }
                
                if (targetPlatform) {
                  requestPayload.target_platform = targetPlatform;
                  console.log('✅ [TARGET_PLATFORM] Set target_platform to:', requestPayload.target_platform);
                } else {
                  console.log('❌ [TARGET_PLATFORM] No platform found, target_platform will be undefined');
                }
              } else if (column.cell_type === 'communication') {
                // COMMUNICATION COLUMN - only include email/conversation fields
                requestPayload.email_provider = column.email_provider;
                requestPayload.communication_sources = column.communication_sources;
              } else if (column.cell_type === 'track_posts') {
                // TRACK POSTS COLUMN - include track_posts configuration
                requestPayload.track_posts_config = column.track_posts_config;
                console.log('🔍 [TRACK_POSTS] Config:', column.track_posts_config);
              }
              
              // Add table_id, column_id, creator_id so backend can write to database
              if (tableId && tableId.trim()) {
                requestPayload.table_id = tableId;
                requestPayload.column_id = column.id;
                requestPayload.creator_id = rowId;
              }
              
              // Add project_id for conversation AI agent context matching
              if (projectId && projectId.trim()) {
                requestPayload.project_id = projectId;
              }
              
              console.log('📤 [ResearchTable] Sending research request:', {
                columnName: column.name,
                cellType: column.cell_type,
                dataType: column.data_type,
                resources,
                hasPrompt: !!column.prompt,
                promptLength: column.prompt?.length || 0,
                labelOptions: column.label_options,
                tableId: tableId,
                tableIdPassed: !!requestPayload.table_id,
                fullColumn: column, // Log the full column object
                requestPayload
              });
              
              let response;
              
              if (column.cell_type === 'track_posts') {
                // TRACK POSTS: Call dedicated post-discovery-agent Lambda
                console.log('🔍 [TRACK_POSTS] Calling post-discovery-agent');
                
                // Get current user ID for the post-discovery-agent
                const currentUser = await userService.getCurrentUser();
                const cognitoUserId = currentUser?.id || 'unknown';
                
                const postDiscoveryPayload = {
                  creator_id: rowId,
                  platform: column.track_posts_config?.platform,
                  search_criteria: column.track_posts_config?.search_criteria,
                  table_id: tableId,
                  column_id: column.id
                };
                
                console.log('📤 [TRACK_POSTS] Post discovery payload:', postDiscoveryPayload);
                
                // Call the post-discovery-agent Lambda directly
                response = await researchAIService.callPostDiscoveryAgent(postDiscoveryPayload);
              } else {
                // NORMAL/COMMUNICATION: Call regular research AI service
                response = await researchAIService.researchCreator(requestPayload);
              }
      
      if (!response.success) {
        // Handle quota/trial errors specially
        if (response.error_type === 'quota_exhausted' || response.error_type === 'trial_expired') {
          // Check if cell already has existing data
          const existingCellData = tableData[rowId]?.[columnId];
          const hasExistingData = existingCellData?.value && existingCellData.value.trim() !== '';
          
          if (hasExistingData) {
            // Protect existing data from being overwritten
            // Restore the original data and show a toast notification
            setTableData(prev => ({
              ...prev,
              [rowId]: {
                ...prev[rowId],
                [columnId]: {
                  ...existingCellData,
                  loading: false
                }
              }
            }));
            
            // Show appropriate notification based on error type
            if (response.error_type === 'trial_expired') {
              message.warning({
                content: '⏰ Trial expired. Subscribe to update cells.',
                duration: 5,
                key: 'trial-expired-notification'
              });
            } else if (response.error_type === 'quota_exhausted') {
              message.warning({
                content: '⚠️ Out of AI tokens. Purchase more to update cells.',
                duration: 5,
                key: 'quota-exhausted-notification'
              });
            }
            
            return; // Exit without overwriting data
          }
          
          // For empty cells or quota exhausted, show error in cell
          const errorData: CellData = { 
            value: '', 
            loading: false, 
            error: response.error || (response.error_type === 'trial_expired' ? 'Free trial expired' : 'Out of AI tokens'),
            error_type: response.error_type as 'quota_exhausted' | 'trial_expired'
          };
          
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [columnId]: errorData
            }
          }));
          
          await saveCellData(rowId, columnId, errorData);
          return; // Exit early for quota/trial errors
        }
        
        throw new Error(response.error || 'Research failed');
      }

      // Handle workflow analysis queuing
      await handleWorkflowAnalysisQueuing(response, rowId);

      // Backend has already written the cell data with correct pending_change determination
      // ALL backends now write to DB - fetch the authoritative state
      console.log(`✅ [ResearchTable] Research API completed for ${rowId}/${column.id}`);
      console.log(`📊 [ResearchTable] Fetching authoritative state from database...`);
      
      // Add small delay to ensure database write has committed, then retry if needed
      let freshCellData = null;
      const maxRetries = 2;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (attempt > 0) {
          // Wait 500ms before retry
          await new Promise(resolve => setTimeout(resolve, 500));
          console.log(`⏳ [ResearchTable] Retry ${attempt} fetching cell data...`);
        } else {
          // First attempt: small delay to ensure write is committed
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        try {
          freshCellData = await researchTableService.getCellData(
            projectId || '',
            currentTable?.id || '',
            rowId,
            columnId
          );
          
          if (freshCellData && (freshCellData.value || freshCellData.pending_change)) {
            break; // Success - exit retry loop
          }
        } catch (error) {
          console.error(`❌ [ResearchTable] Error fetching cell data (attempt ${attempt + 1}):`, error);
          if (attempt === maxRetries - 1) {
            throw error;
          }
        }
      }
      
      if (freshCellData) {
        console.log(`✅ [ResearchTable] Fetched fresh cell data from DB:`, {
          value: freshCellData.value,
          has_pending: !!freshCellData.pending_change,
          pending_value: freshCellData.pending_change?.value
        });
        
        // Convert CreatorCellData to CellData (handle type differences)
        const convertedCellData: CellData = {
          ...freshCellData,
          error: freshCellData.error || undefined, // Convert null to undefined
          loading: false,
          fetch_time: Date.now()
        };
        
        // Update cell with the authoritative data from backend
        setTableData(prev => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            [columnId]: convertedCellData
          }
        }));
      } else {
        // Failed to fetch from database - show error
        console.error(`❌ [ResearchTable] Failed to fetch cell data from database after research completed`);
        console.error(`❌ [ResearchTable] This means backend write may have failed`);
        
        // Show error in cell
        setTableData(prev => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            [columnId]: {
              value: '',
              loading: false,
              error: 'Failed to save result to database',
              fetch_time: Date.now()
            }
          }
        }));
      }

    } catch (error) {
      console.error('🚨 [ResearchTable] Research error details:', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        rowId,
        columnId,
        column: {
          name: column?.name,
          cell_type: column?.cell_type,
          data_type: column?.data_type,
          prompt: column?.prompt,
          communication_sources: column?.communication_sources,
          requires_thread: column?.requires_thread
        }
      });
      
      const errorCellData = { 
        value: '', 
        loading: false, 
        error: error instanceof Error ? error.message : 'Research failed'
      };

      setTableData(prev => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          [columnId]: errorCellData
        }
      }));

      // Persist error state to backend
      await saveCellData(rowId, columnId, errorCellData);
    }
  };

  const handleDeleteShopifyDiscountCode = async (rowId: string, columnId: string) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;

    // Get discount code from cell data
    const discountCode = tableData[rowId]?.[columnId]?.meta?.shopify_discount_code || 
                        tableData[rowId]?.[columnId]?.value;
    
    if (!discountCode) {
      message.warning('No discount code to delete');
      return;
    }

    if (!shopifyChannelId) {
      message.error('Shopify not connected');
      return;
    }

    try {
      // Call delete API
      await shopifyActionsService.deleteCreatorCode({
        creator_id: row.creator.id,
        code: discountCode,
        channel_id: shopifyChannelId,
        project_uuid: currentTable?.project_id || currentTable?.project_info?.project_uuid
      });

      // Clear cell data
      const clearedCell: CellData = {
        value: '',
        loading: false,
        error: undefined,
        meta: undefined
      };

      // Update local state
      setTableData(prev => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          [columnId]: clearedCell
        }
      }));

      // Persist to backend
      await saveCellData(rowId, columnId, clearedCell);

      message.success('Discount code deleted successfully');
    } catch (error) {
      console.error('[handleDeleteShopifyDiscountCode] Error:', error);
      message.error('Failed to delete discount code');
    }
  };

  const handleResearchAllInColumn = async (columnId: string) => {
    console.log('🔬 [handleResearchAllInColumn] tableId prop value:', tableId, 'type:', typeof tableId);
    const column = columns.find(c => c.id === columnId);
    if (!column || rows.length === 0) return;
    
    // Only allow research for normal, communication, and track_posts columns (not outreach)
    if (column.cell_type !== 'normal' && column.cell_type !== 'communication' && column.cell_type !== 'track_posts') {
      console.log('Batch research skipped - not a research column');
      return;
    }

    // Filter rows based on column dependencies only - let AI handle communication availability
    const eligibleRows = rows.filter(row => {
      // Check column dependencies
      if (column.depends_on) {
        const dependencyValue = tableData[row.id]?.[column.depends_on.column_id]?.value;
        if (!dependencyValue || !column.depends_on.required_values.includes(dependencyValue)) {
          return false;
        }
      }
      
      return true;
    });

    // Only set loading state for eligible cells (not disabled ones)
    const updatedTableData = { ...tableData };
    eligibleRows.forEach(row => {
      if (!updatedTableData[row.id]) updatedTableData[row.id] = {};
      updatedTableData[row.id][columnId] = { 
        ...updatedTableData[row.id][columnId], 
        loading: true, 
        error: undefined 
      };
    });
    setTableData(updatedTableData);
    
    console.log(`🔍 [ResearchTable] Batch research - ${eligibleRows.length}/${rows.length} creators eligible for ${column.name}`);
    
    const researchPromises = eligibleRows.map(async (row) => {
      try {
        const platformData = getCreatorPrimaryPlatform(row.creator);
        
        // Check if creator has multiple platforms
        const platforms = [];
        const handles: Record<string, string> = {};
        
        if (row.creator.tiktok_data && row.creator.tiktok_data.length > 0) {
          platforms.push('tiktok');
          handles.tiktok = row.creator.tiktok_data[0].handle;
        }
        if (row.creator.instagram_data && row.creator.instagram_data.length > 0) {
          platforms.push('instagram');
          handles.instagram = row.creator.instagram_data[0].handle;
        }
        if (row.creator.twitch_data && row.creator.twitch_data.length > 0) {
          platforms.push('twitch');
          handles.twitch = row.creator.twitch_data[0].handle;
        }
        
        const creatorInfo: CreatorInfo = platforms.length > 1 ? {
          // Multi-platform creator
          id: row.creator.id, // Add creator ID
          name: platformData.name,
          handle: platformData.handle, // Primary handle for display
          platform: 'multi',
          followers_count: platformData.followers_count,
          engagement_rate: platformData.engagement_rate,
          handles: handles // Include all platform handles
        } : {
          // Single platform creator
          id: row.creator.id, // Add creator ID
          name: platformData.name,
          handle: platformData.handle,
          platform: platformData.platform,
          followers_count: platformData.followers_count,
          engagement_rate: platformData.engagement_rate
        };
        
        // Build context from previous columns for this specific creator
        const existingData = tableData[row.id] || {};
        const previousResearch = columns
          .filter(col => col.id !== columnId && existingData[col.id]?.value && !existingData[col.id]?.error)
          .map(col => `${col.name}: ${existingData[col.id].value}`)
          .join('\n');
        
        const contextWithPreviousData = `Creator research for @${platformData.handle} on ${platformData.platform}` + 
          (previousResearch ? `\n\nPreviously researched data for this creator:\n${previousResearch}` : '');
        
        // Only include project_id if it actually exists and is not empty
        const projectId = currentTable?.project_id || currentTable?.project_info?.project_uuid;
        
        // Determine resources based on column type
        const resources = (column.cell_type === 'communication' ) && column.communication_sources 
          ? column.communication_sources 
          : getDefaultResearchSources(platformData.platform, row.creator, column);
        
        // For outreach columns, include the template content in the context
        let enhancedContext = contextWithPreviousData;
          if (false) {
            // This code is no longer used since outreach columns are removed
          }

        // Ensure outreach columns have a prompt and labels (fallback for existing columns)
        let effectivePrompt = column.prompt || '';
        let effectiveLabels = column.label_options || [];
        
        if (false) {
          if (!effectivePrompt) {
            effectivePrompt = 'Check if this template or similar content has been sent to this creator based on email conversations. Look for emails that contain the same key information, campaign details, or business proposal as the template below, even if the wording is different or template variables were replaced. Consider it "Sent" if you find emails with substantially similar content, structure, or campaign details. Return "Ready to Send" only if no similar content has been sent yet.';
          }
          if (!effectiveLabels.length) {
            effectiveLabels = ['Ready to Send', 'Sent'];
          }
        }
        
        // Build request payload based on column type
        const requestPayload: any = {
          prompt: effectivePrompt,
          creator_info: creatorInfo,
          resources: resources,
          context: enhancedContext,
          response_format: column.response_format,
          data_type: column.data_type,
          label_options: effectiveLabels
        };

        // Add column-type-specific fields
        if (column.cell_type === 'normal') {
          // PLATFORM COLUMN - only include platform resources
          requestPayload.platform_resources = column.platform_resources || [];
          
          // Add target platform - try platform_icon first, then extract from platform_resources
          let targetPlatform = column.platform_icon;
          if (!targetPlatform && column.platform_resources && column.platform_resources.length > 0) {
            const firstResource = column.platform_resources[0];
            if (firstResource.platform) {
              targetPlatform = firstResource.platform;
            }
          }
          if (targetPlatform) {
            requestPayload.target_platform = targetPlatform;
          }
        } else if (column.cell_type === 'communication') {
          // COMMUNICATION COLUMN - only include email/conversation fields
          requestPayload.email_provider = column.email_provider;
          requestPayload.communication_sources = column.communication_sources;
        } else if (column.cell_type === 'track_posts') {
          // TRACK POSTS COLUMN - include track_posts configuration
          requestPayload.track_posts_config = column.track_posts_config;
        }
        
        // Only add project_id if it exists and is not empty
        if (projectId && projectId.trim()) {
          requestPayload.project_id = projectId;
        }
        
        // Add table_id, column_id, creator_id so backend can write to database
        if (tableId && tableId.trim()) {
          requestPayload.table_id = tableId;
          requestPayload.column_id = columnId;
          requestPayload.creator_id = row.id;
        }
        
        let response;
        
        if (column.cell_type === 'track_posts') {
          // TRACK POSTS: Call dedicated post-discovery-agent Lambda
          console.log('🔍 [BATCH-TRACK_POSTS] Calling post-discovery-agent for', row.id);
          
          // Get current user ID for the post-discovery-agent
          const currentUser = await userService.getCurrentUser();
          const cognitoUserId = currentUser?.id || 'unknown';
          
          const postDiscoveryPayload = {
            creator_id: row.id,
            platform: column.track_posts_config?.platform,
            search_criteria: column.track_posts_config?.search_criteria,
            table_id: tableId,
            column_id: columnId
          };
          
          response = await researchAIService.callPostDiscoveryAgent(postDiscoveryPayload);
        } else {
          // NORMAL/COMMUNICATION: Call regular research AI service
          response = await researchAIService.researchCreator(requestPayload);
        }
        
        if (!response.success) {
          throw new Error(response.error || 'Research failed');
        }
        
        // Handle workflow analysis queuing
        await handleWorkflowAnalysisQueuing(response, row.id);
        
        // Backend has already written the cell data - fetch from database
        console.log(`✅ [handleResearchAllInColumn] Research completed for ${row.id}/${columnId}`);
        console.log(`📊 [handleResearchAllInColumn] Fetching from database...`);
        
        const freshCellData = await researchTableService.getCellData(
          projectId || '',
          currentTable?.id || '',
          row.id,
          columnId
        );
        
        if (freshCellData) {
          console.log(`✅ [handleResearchAllInColumn] Fetched fresh cell data:`, {
            value: freshCellData.value,
            has_pending: !!freshCellData.pending_change
          });
          
          // Convert and update
          const convertedCellData: CellData = {
            ...freshCellData,
            error: freshCellData.error || undefined,
            loading: false,
            fetch_time: Date.now()
          };
          
          setTableData(prev => ({
            ...prev,
            [row.id]: {
              ...prev[row.id],
              [columnId]: convertedCellData
            }
          }));
        } else {
          console.error(`❌ [handleResearchAllInColumn] Failed to fetch from database`);
          
          // Show error in cell
          setTableData(prev => ({
            ...prev,
            [row.id]: {
              ...prev[row.id],
              [columnId]: {
                value: '',
                loading: false,
                error: 'Failed to save to database',
                fetch_time: Date.now()
              }
            }
          }));
        }
        
      } catch (error) {
        console.error(`Research failed for creator ${row.creator
          
        }:`, error);
        
        const errorCellData = {
          value: '',
          loading: false,
          error: error instanceof Error ? error.message : 'Research failed'
        };

        // Update this specific cell with error immediately
        setTableData(prev => ({
          ...prev,
          [row.id]: {
            ...prev[row.id],
            [columnId]: errorCellData
          }
        }));

        // Save error state to backend
        await saveCellData(row.id, columnId, errorCellData);
      }
    });

    // Wait for all research to complete
    await Promise.allSettled(researchPromises);
  };

  const handleResearchAllInRow = async (rowId: string) => {
    const row = rows.find(r => r.id === rowId);
    if (!row || columns.length === 0) return;
    
    // Filter columns that can be researched (normal, communication, outreach, shopify, relationship)
    const eligibleColumns = columns.filter(column => {
      // Allow research for normal, communication, outreach, shopify, and relationship columns
      if (column.cell_type !== 'normal' && 
          column.cell_type !== 'communication' && 
          column.cell_type !== 'outreach' && 
          column.cell_type !== 'shopify' && 
          column.cell_type !== 'relationship') {
        return false;
      }
      
      // For Shopify discount_code columns, skip if code already exists (don't regenerate)
      if (column.cell_type === 'shopify' && column.shopify_config?.subtype === 'discount_code') {
        const existingCode = tableData[rowId]?.[column.id]?.value;
        if (existingCode) {
          console.log(`Skipping discount_code column "${column.name}" - code already exists`);
          return false;
        }
      }
      
      // For Shopify analytics columns, skip if no discount code exists for this creator
      if (column.cell_type === 'shopify' && column.shopify_config?.subtype === 'analytics') {
        const hasDiscountCode = columns.some(col => 
          col.cell_type === 'shopify' && 
          col.shopify_config?.subtype === 'discount_code' && 
          tableData[rowId]?.[col.id]?.value
        );
        if (!hasDiscountCode) {
          console.log(`Skipping analytics column "${column.name}" - no discount code exists`);
          return false;
        }
      }
      
      // Check column dependencies
      if (column.depends_on) {
        const dependencyValue = tableData[rowId]?.[column.depends_on.column_id]?.value;
        if (!dependencyValue || !column.depends_on.required_values.includes(dependencyValue)) {
          return false;
        }
      }
      
      // Check if communication column requires a thread (needs email to lookup conversations)
      if (column.cell_type === 'communication' && column.requires_thread) {
        // Check if this creator has an email to lookup conversations
        const hasEmail = row.creator.contacts?.some(c => c.type === 'email') || 
                         row.creator.creator_shipping?.email;
        if (!hasEmail) {
          return false;
        }
      }
      
      return true;
    });
    
    if (eligibleColumns.length === 0) {
      console.log('Row research skipped - no eligible columns');
      return;
    }

    // Set loading state for all eligible cells in this row
    const updatedTableData = { ...tableData };
    if (!updatedTableData[rowId]) updatedTableData[rowId] = {};
    
    eligibleColumns.forEach(column => {
      updatedTableData[rowId][column.id] = { 
        ...updatedTableData[rowId][column.id], 
        loading: true, 
        error: undefined 
      };
    });
    
    setTableData(updatedTableData);
    
    console.log(`🔍 [ResearchTable] Row research - ${eligibleColumns.length} columns eligible for creator ${getCreatorPrimaryPlatform(row.creator).handle}`);
    
    // Research all eligible columns for this row in parallel
    const columnResearchPromises = eligibleColumns.map(async (column) => {
      try {
        // For Shopify and relationship columns, use handleResearchCell directly
        if (column.cell_type === 'shopify' || column.cell_type === 'relationship') {
          await handleResearchCell(rowId, column.id);
          return;
        }
        
        const platformData = getCreatorPrimaryPlatform(row.creator);
        
        // Check if creator has multiple platforms
        const platforms = [];
        const handles: Record<string, string> = {};
        
        if (row.creator.tiktok_data && row.creator.tiktok_data.length > 0) {
          platforms.push('tiktok');
          handles.tiktok = row.creator.tiktok_data[0].handle;
        }
        if (row.creator.instagram_data && row.creator.instagram_data.length > 0) {
          platforms.push('instagram');
          handles.instagram = row.creator.instagram_data[0].handle;
        }
        if (row.creator.twitch_data && row.creator.twitch_data.length > 0) {
          platforms.push('twitch');
          handles.twitch = row.creator.twitch_data[0].handle;
        }
        
        const creatorInfo: CreatorInfo = platforms.length > 1 ? {
          // Multi-platform creator
          id: row.creator.id, // Add creator ID
          name: platformData.name,
          handle: platformData.handle, // Primary handle for display
          platform: 'multi',
          followers_count: platformData.followers_count,
          engagement_rate: platformData.engagement_rate,
          handles: handles // Include all platform handles
        } : {
          // Single platform creator
          id: row.creator.id, // Add creator ID
          name: platformData.name,
          handle: platformData.handle,
          platform: platformData.platform,
          followers_count: platformData.followers_count,
          engagement_rate: platformData.engagement_rate
        };
        
        // Build context from existing data in the table (parallel execution, no local accumulator)
        const existingRowData = tableData[rowId] || {};
        const previousResearch = columns
          .filter(col => col.id !== column.id && existingRowData[col.id]?.value && !existingRowData[col.id]?.error)
          .map(col => `${col.name}: ${existingRowData[col.id].value}`)
          .join('\n');
        
        const contextWithPreviousData = `Creator research for @${platformData.handle} on ${platformData.platform}` + 
          (previousResearch ? `\n\nPreviously researched data for this creator:\n${previousResearch}` : '');
        
        console.log(`🔍 [ResearchTable] Parallel research for ${platformData.handle} - Column: ${column.name}`);
        
        // Only include project_id if it actually exists and is not empty
        const projectId = currentTable?.project_id || currentTable?.project_info?.project_uuid;
        
        // Determine resources based on column type
        const resources = (column.cell_type === 'communication' ) && column.communication_sources 
          ? column.communication_sources 
          : getDefaultResearchSources(platformData.platform, row.creator, column);
        
        // For outreach columns, include the template content in the context
        let enhancedContext = contextWithPreviousData;
          if (false) {
            // This code is no longer used since outreach columns are removed
          }

        // Ensure outreach columns have a prompt and labels (fallback for existing columns)
        let effectivePrompt = column.prompt || '';
        let effectiveLabels = column.label_options || [];
        
        if (false) {
          if (!effectivePrompt) {
            effectivePrompt = 'Check if this template or similar content has been sent to this creator based on email conversations. Look for emails that contain the same key information, campaign details, or business proposal as the template below, even if the wording is different or template variables were replaced. Consider it "Sent" if you find emails with substantially similar content, structure, or campaign details. Return "Ready to Send" only if no similar content has been sent yet.';
          }
          if (!effectiveLabels.length) {
            effectiveLabels = ['Ready to Send', 'Sent'];
          }
        }
        
        // Build request payload based on column type
        const requestPayload: any = {
          prompt: effectivePrompt,
          creator_info: creatorInfo,
          resources: resources,
          context: enhancedContext,
          response_format: column.response_format,
          data_type: column.data_type,
          label_options: effectiveLabels
        };

        // Add column-type-specific fields
        if (column.cell_type === 'normal') {
          // PLATFORM COLUMN - only include platform resources
          requestPayload.platform_resources = column.platform_resources || [];
          
          // Add target platform - try platform_icon first, then extract from platform_resources
          let targetPlatform = column.platform_icon;
          if (!targetPlatform && column.platform_resources && column.platform_resources.length > 0) {
            const firstResource = column.platform_resources[0];
            if (firstResource.platform) {
              targetPlatform = firstResource.platform;
            }
          }
          if (targetPlatform) {
            requestPayload.target_platform = targetPlatform;
          }
        } else if (column.cell_type === 'communication') {
          // COMMUNICATION COLUMN - only include email/conversation fields
          requestPayload.email_provider = column.email_provider;
          requestPayload.communication_sources = column.communication_sources;
        } else if (column.cell_type === 'track_posts') {
          // TRACK POSTS COLUMN - include track_posts configuration
          requestPayload.track_posts_config = column.track_posts_config;
        }
        
        // Only add project_id if it exists and is not empty
        if (projectId && projectId.trim()) {
          requestPayload.project_id = projectId;
        }
        
        // Add table_id, column_id, creator_id so backend can write to database
        if (tableId && tableId.trim()) {
          requestPayload.table_id = tableId;
          requestPayload.column_id = column.id;
          requestPayload.creator_id = rowId;
        }
        
        let response;
        
        if (column.cell_type === 'track_posts') {
          // TRACK POSTS: Call dedicated post-discovery-agent Lambda
          console.log('🔍 [ROW-TRACK_POSTS] Calling post-discovery-agent for', rowId, column.id);
          
          // Get current user ID for the post-discovery-agent
          const currentUser = await userService.getCurrentUser();
          const cognitoUserId = currentUser?.id || 'unknown';
          
          const postDiscoveryPayload = {
            creator_id: rowId,
            platform: column.track_posts_config?.platform,
            search_criteria: column.track_posts_config?.search_criteria,
            table_id: tableId,
            column_id: column.id,
          };
          
          response = await researchAIService.callPostDiscoveryAgent(postDiscoveryPayload);
        } else {
          // NORMAL/COMMUNICATION: Call regular research AI service
          response = await researchAIService.researchCreator(requestPayload);
        }
        
        if (!response.success) {
          throw new Error(response.error || 'Research failed');
        }
        
        // Handle workflow analysis queuing
        await handleWorkflowAnalysisQueuing(response, rowId);
        
        // Backend has already written the cell data - fetch from database
        console.log(`✅ [handleResearchAllInRow] Research completed for ${rowId}/${column.id}`);
        console.log(`📊 [handleResearchAllInRow] Fetching from database...`);
        
        const freshCellData = await researchTableService.getCellData(
          projectId || '',
          currentTable?.id || '',
          rowId,
          column.id
        );
        
        if (freshCellData) {
          console.log(`✅ [handleResearchAllInRow] Fetched fresh cell data:`, {
            value: freshCellData.value,
            has_pending: !!freshCellData.pending_change
          });
          
          // Convert and update
          const convertedCellData: CellData = {
            ...freshCellData,
            error: freshCellData.error || undefined,
            loading: false,
            fetch_time: Date.now()
          };
          
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [column.id]: convertedCellData
            }
          }));
          
          console.log(`✅ [handleResearchAllInRow] Completed column ${column.name}: "${freshCellData.value}"`);
        } else {
          console.error(`❌ [handleResearchAllInRow] Failed to fetch from database`);
          
          // Show error in cell
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [column.id]: {
                value: '',
                loading: false,
                error: 'Failed to save to database',
                fetch_time: Date.now()
              }
            }
          }));
        }
        
      } catch (error) {
        console.error(`Research failed for creator ${getCreatorPrimaryPlatform(row.creator).handle} column ${column.name}:`, error);
        
        const errorCellData = {
          value: '',
          loading: false,
          error: error instanceof Error ? error.message : 'Research failed'
        };

        // Update this specific cell with error immediately
        setTableData(prev => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            [column.id]: errorCellData
          }
        }));

        // Save error state to backend
        await saveCellData(rowId, column.id, errorCellData);
      }
    });

    // Wait for all column research to complete
    await Promise.allSettled(columnResearchPromises);
  };

  const handleResearchEmptyInRow = async (rowId: string) => {
    const row = rows.find(r => r.id === rowId);
    if (!row || columns.length === 0) return;
    
    // Filter columns that can be researched (normal, communication, outreach, shopify, relationship)
    const eligibleColumns = columns.filter(column => {
      // Allow research for normal, communication, outreach, shopify, and relationship columns
      if (column.cell_type !== 'normal' && 
          column.cell_type !== 'communication' && 
          column.cell_type !== 'outreach' && 
          column.cell_type !== 'shopify' && 
          column.cell_type !== 'relationship') {
        return false;
      }
      
      // For Shopify analytics columns, skip if no discount code exists for this creator
      if (column.cell_type === 'shopify' && column.shopify_config?.subtype === 'analytics') {
        const hasDiscountCode = columns.some(col => 
          col.cell_type === 'shopify' && 
          col.shopify_config?.subtype === 'discount_code' && 
          tableData[rowId]?.[col.id]?.value
        );
        if (!hasDiscountCode) {
          console.log(`Skipping analytics column "${column.name}" - no discount code exists`);
          return false;
        }
      }
      
      // Check column dependencies
      if (column.depends_on) {
        const dependencyValue = tableData[rowId]?.[column.depends_on.column_id]?.value;
        if (!dependencyValue || !column.depends_on.required_values.includes(dependencyValue)) {
          return false;
        }
      }
      
      // Check if communication column requires a thread (needs email to lookup conversations)
      if (column.cell_type === 'communication' && column.requires_thread) {
        // Check if this creator has an email to lookup conversations
        const hasEmail = row.creator.contacts?.some(c => c.type === 'email') || 
                         row.creator.creator_shipping?.email;
        if (!hasEmail) {
          return false;
        }
      }
      
      // ONLY RESEARCH CELLS THAT ARE EMPTY (this is the key difference from handleResearchAllInRow)
      const cellData = tableData[rowId]?.[column.id];
      const isEmpty = !cellData || !cellData.value || cellData.value.trim() === '';
      
      return isEmpty;
    });
    
    if (eligibleColumns.length === 0) {
      console.log('Row research skipped - no empty cells to research');
      return;
    }

    // Set loading state for all empty cells in this row
    const updatedTableData = { ...tableData };
    if (!updatedTableData[rowId]) updatedTableData[rowId] = {};
    
    eligibleColumns.forEach(column => {
      updatedTableData[rowId][column.id] = { 
        ...updatedTableData[rowId][column.id], 
        loading: true, 
        error: undefined 
      };
    });
    
    setTableData(updatedTableData);
    
    console.log(`🔍 [ResearchTable] Row research (empty only) - ${eligibleColumns.length} empty columns for creator ${getCreatorPrimaryPlatform(row.creator).handle}`);
    
    // Research all empty columns for this row in parallel
    const columnResearchPromises = eligibleColumns.map(async (column) => {
      try {
        // For Shopify and relationship columns, use handleResearchCell directly
        if (column.cell_type === 'shopify' || column.cell_type === 'relationship') {
          await handleResearchCell(rowId, column.id);
          return;
        }
        
        const platformData = getCreatorPrimaryPlatform(row.creator);
        
        // Check if creator has multiple platforms
        const platforms = [];
        const handles: Record<string, string> = {};
        
        if (row.creator.tiktok_data && row.creator.tiktok_data.length > 0) {
          platforms.push('tiktok');
          handles.tiktok = row.creator.tiktok_data[0].handle;
        }
        if (row.creator.instagram_data && row.creator.instagram_data.length > 0) {
          platforms.push('instagram');
          handles.instagram = row.creator.instagram_data[0].handle;
        }
        if (row.creator.twitch_data && row.creator.twitch_data.length > 0) {
          platforms.push('twitch');
          handles.twitch = row.creator.twitch_data[0].handle;
        }
        
        const creatorInfo: CreatorInfo = platforms.length > 1 ? {
          // Multi-platform creator
          id: row.creator.id,
          name: platformData.name,
          handle: platformData.handle,
          platform: 'multi',
          followers_count: platformData.followers_count,
          engagement_rate: platformData.engagement_rate,
          handles: handles
        } : {
          // Single platform creator
          id: row.creator.id,
          name: platformData.name,
          handle: platformData.handle,
          platform: platformData.platform,
          followers_count: platformData.followers_count,
          engagement_rate: platformData.engagement_rate
        };
        
        // Build context from existing data in the table
        const existingRowData = tableData[rowId] || {};
        const previousResearch = columns
          .filter(col => col.id !== column.id && existingRowData[col.id]?.value && !existingRowData[col.id]?.error)
          .map(col => `${col.name}: ${existingRowData[col.id].value}`)
          .join('\n');
        
        const contextWithPreviousData = `Creator research for @${platformData.handle} on ${platformData.platform}` + 
          (previousResearch ? `\n\nPreviously researched data for this creator:\n${previousResearch}` : '');
        
        console.log(`🔍 [ResearchTable] Empty cells research for ${platformData.handle} - Column: ${column.name}`);
        
        const projectId = currentTable?.project_id || currentTable?.project_info?.project_uuid;
        
        // Determine resources based on column type
        const resources = (column.cell_type === 'communication' ) && column.communication_sources 
          ? column.communication_sources 
          : getDefaultResearchSources(platformData.platform, row.creator, column);
        
        let enhancedContext = contextWithPreviousData;

        // Ensure outreach columns have a prompt and labels (fallback for existing columns)
        let effectivePrompt = column.prompt || '';
        let effectiveLabels = column.label_options || [];
        
        // Build request payload
        const requestPayload: any = {
          prompt: effectivePrompt,
          creator_info: creatorInfo,
          resources: resources,
          context: enhancedContext,
          response_format: column.response_format,
          data_type: column.data_type,
          label_options: effectiveLabels
        };

        // Add column-type-specific fields
        if (column.cell_type === 'normal') {
          requestPayload.platform_resources = column.platform_resources || [];
          
          let targetPlatform = column.platform_icon;
          if (!targetPlatform && column.platform_resources && column.platform_resources.length > 0) {
            const firstResource = column.platform_resources[0];
            if (firstResource.platform) {
              targetPlatform = firstResource.platform;
            }
          }
          if (targetPlatform) {
            requestPayload.target_platform = targetPlatform;
          }
        } else if (column.cell_type === 'communication') {
          requestPayload.email_provider = column.email_provider;
          requestPayload.communication_sources = column.communication_sources;
        } else if (column.cell_type === 'track_posts') {
          // TRACK POSTS COLUMN - include track_posts configuration
          requestPayload.track_posts_config = column.track_posts_config;
        }
        
        if (projectId && projectId.trim()) {
          requestPayload.project_id = projectId;
        }
        
        // Add table_id, column_id, creator_id so backend can write to database
        if (tableId && tableId.trim()) {
          requestPayload.table_id = tableId;
          requestPayload.column_id = column.id;
          requestPayload.creator_id = rowId;
        }
        
        let response;
        
        if (column.cell_type === 'track_posts') {
          // TRACK POSTS: Call dedicated post-discovery-agent Lambda
          console.log('🔍 [EMPTY-TRACK_POSTS] Calling post-discovery-agent for', rowId, column.id);
          
          // Get current user ID for the post-discovery-agent
          const currentUser = await userService.getCurrentUser();
          const cognitoUserId = currentUser?.id || 'unknown';
          
          const postDiscoveryPayload = {
            creator_id: rowId,
            platform: column.track_posts_config?.platform,
            search_criteria: column.track_posts_config?.search_criteria,
            table_id: tableId,
            column_id: column.id,
          };
          
          response = await researchAIService.callPostDiscoveryAgent(postDiscoveryPayload);
        } else {
          // NORMAL/COMMUNICATION: Call regular research AI service
          response = await researchAIService.researchCreator(requestPayload);
        }
        
        if (!response.success) {
          throw new Error(response.error || 'Research failed');
        }
        
        // Handle workflow analysis queuing
        await handleWorkflowAnalysisQueuing(response, rowId);
        
        // Backend has already written the cell data - fetch from database
        console.log(`✅ [handleRunAllEmptyCells] Research completed for ${rowId}/${column.id}`);
        console.log(`📊 [handleRunAllEmptyCells] Fetching from database...`);
        
        const freshCellData = await researchTableService.getCellData(
          projectId || '',
          currentTable?.id || '',
          rowId,
          column.id
        );
        
        if (freshCellData) {
          console.log(`✅ [handleRunAllEmptyCells] Fetched fresh cell data:`, {
            value: freshCellData.value,
            has_pending: !!freshCellData.pending_change
          });
          
          // Convert and update
          const convertedCellData: CellData = {
            ...freshCellData,
            error: freshCellData.error || undefined,
            loading: false,
            fetch_time: Date.now()
          };
          
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [column.id]: convertedCellData
            }
          }));
          
          console.log(`✅ [handleRunAllEmptyCells] Completed column ${column.name}: "${freshCellData.value}"`);
        } else {
          console.error(`❌ [handleRunAllEmptyCells] Failed to fetch from database`);
          
          // Show error in cell
          setTableData(prev => ({
            ...prev,
            [rowId]: {
              ...prev[rowId],
              [column.id]: {
                value: '',
                loading: false,
                error: 'Failed to save to database',
                fetch_time: Date.now()
              }
            }
          }));
        }
        
      } catch (error) {
        console.error(`Research failed for creator ${getCreatorPrimaryPlatform(row.creator).handle} column ${column.name}:`, error);
        
        const errorCellData = {
          value: '',
          loading: false,
          error: error instanceof Error ? error.message : 'Research failed'
        };

        // Update this specific cell with error immediately
        setTableData(prev => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            [column.id]: errorCellData
          }
        }));

        // Save error state to backend
        await saveCellData(rowId, column.id, errorCellData);
      }
    });

    // Wait for all column research to complete
    await Promise.allSettled(columnResearchPromises);
  };

  const handleResearchAllRows = async () => {
    if (rows.length === 0 || columns.length === 0) return;
    
    // Filter rows that have at least one researchable column
    const eligibleRows = rows.filter(row => {
      return columns.some(column => {
        // Only allow research for normal, communication, and outreach columns
        if (column.cell_type !== 'normal' && column.cell_type !== 'communication' && column.cell_type !== 'outreach') {
          return false;
        }
        
        // Skip disabled cells
        if (isCommunicationCellDisabled(row.creator.id, column)) {
          return false;
        }
        
        // Check column dependencies
        if (column.depends_on) {
          const dependencyValue = tableData[row.id]?.[column.depends_on.column_id]?.value;
          if (!dependencyValue || !column.depends_on.required_values.includes(dependencyValue)) {
            return false;
          }
        }
        
        // For conversation-based columns, check if creator has valid email
        if (column.cell_type === 'communication' ) {
          const contactInfo = row.creator.contacts?.find(c => c.type === 'email')?.value || 
                             row.creator.creator_shipping?.email;
          
          if (!contactInfo || 
              contactInfo.includes('noemail_') || 
              contactInfo.includes('unknown.com') || 
              contactInfo.includes('@example.com')) {
            return false;
          }
        }
        
        return true;
      });
    });

    if (eligibleRows.length === 0) {
      console.log('Bulk row research skipped - no eligible rows');
      return;
    }

    console.log(`🔍 [ResearchTable] Bulk row research - ${eligibleRows.length}/${rows.length} rows eligible`);
    
    // Run all rows in parallel, but each row's columns run sequentially
    const rowResearchPromises = eligibleRows.map(async (row) => {
      return handleResearchAllInRow(row.id);
    });

    // Wait for all rows to complete
    await Promise.allSettled(rowResearchPromises);
  };

  const handleResearchUnrunRows = async () => {
    if (rows.length === 0 || columns.length === 0) return;
    
    console.log(`🔍 [ResearchTable] Bulk unrun research - processing ${rows.length} rows`);
    
    // Run all rows in parallel, but each row processes only empty cells
    const rowResearchPromises = rows.map(async (row) => {
      return handleResearchEmptyInRow(row.id);
    });

    // Wait for all rows to complete
    await Promise.allSettled(rowResearchPromises);
  };

  const handleEditCreator = (creator: Creator) => {
    setEditingCreator(creator);
    setCreatorModalOpen(true);
  };

  const handleAddCreatorManually = () => {
    // Open the creator library extension by dispatching global event
    window.dispatchEvent(new CustomEvent('openCreatorLibrary'));
    console.log('Add creators manually triggered - opening creator library');
  };

  const handleRemoveCreator = (creatorId: string) => {
    const creator = rows.find(row => row.creator.id === creatorId)?.creator;
    if (creator) {
      setCreatorToDelete(creator);
      setDeleteConfirmOpen(true);
    }
  };

  const handleConfirmDeleteCreator = async () => {
    if (!creatorToDelete || !currentTable?.id) return;
    
    try {
      const projectId = currentTable.project_id || currentTable.project_info?.project_uuid;
      
      if (projectId) {
        // Remove from project first
        await removeCreatorFromProject(creatorToDelete.id, projectId);
      }
      
      // Remove from table
      await researchTableService.removeCreatorFromTable(currentTable.id, creatorToDelete.id);
      
      // Reload both creators and table data
      await loadTableCreators(currentTable.id);
      await loadTableData(currentTable.id);
      
      // Close modal and reset state
      setDeleteConfirmOpen(false);
      setCreatorToDelete(null);
      
    } catch (error) {
      console.error('❌ [ResearchTable] Error removing creator:', error);
      message.error('Failed to remove creator');
    }
  };

  const handleCancelDeleteCreator = () => {
    setDeleteConfirmOpen(false);
    setCreatorToDelete(null);
  };

  const handleCloseCreatorModal = () => {
    setCreatorModalOpen(false);
    setEditingCreator(null);
  };

  const handleCreatorModalSuccess = async () => {
    // Refresh both creators and table data to show updated creator info
    if (tableId) {
      await loadTableCreators(tableId);
      await loadTableData(tableId);
    }
    handleCloseCreatorModal();
    message.success('Creator updated successfully');
  };

  // Table context handlers
  const handleUpdateTableContext = async (context: string, goal: string) => {
    if (!currentTable) return;
    
    try {
      // Update the table config with new context and goal
      const updatedConfig = {
        ...currentTable.table_config,
        table_context: context,
        table_goal: goal
      };
      
      await researchTableService.saveTableConfig(currentTable.id, updatedConfig as ResearchTableConfig);
      
      // Update local state
      setCurrentTable({
        ...currentTable,
        table_config: updatedConfig
      });
      
      message.success('Table context updated');
    } catch (error) {
      console.error('❌ [ResearchTable] Error updating table context:', error);
      message.error('Failed to update table context');
    }
  };
  
  const handleUpdateCompanionSettings = async (companionEnabled: boolean, companionTableGoal: string) => {
    if (!currentTable || !tableId) return;
    
    try {
      console.log('🤖 [ResearchTable] Updating companion settings:', { companionEnabled, companionTableGoal });
      
      // Use the service method to update companion settings
      const updatedTable = await researchTableService.updateCompanionSettings(
        tableId, 
        companionEnabled, 
        companionTableGoal
      );
      
      // Update local state
      setCurrentTable(updatedTable);
      
      const statusMessage = companionEnabled 
        ? 'Companion enabled for this table' 
        : 'Companion disabled for this table';
      message.success(statusMessage);
    } catch (error) {
      console.error('Error updating companion settings:', error);
      message.error('Failed to update companion settings');
    }
  };

  const handleOpenTemplates = () => {
    // TODO: Implement template selection modal
    message.info('Template selection will be available soon');
  };

  // File upload handlers
  const handleFileUpload = async (file: File) => {
    
    if (uploadMode) {
      return handleUploadModeFileUpload(file);
    }

    message.error('Excel import functionality has been removed. Please use the Creator Library to add creators.');
    return;
  };

  // Special file upload handler for upload mode
  const handleUploadModeFileUpload = async (file: File) => {
    message.error('Excel import functionality has been removed. Please use the Creator Library to add creators.');
    setUploadProcessing(false);
    return;
  };

  // Function to create table with selected creators from mapping
  const handleCreateTableWithMappedCreators = async (selectedEntries: any[]) => {
    message.error('Excel import functionality has been removed. Please use the Creator Library to add creators.');
    return;
  };





  return (
    <div style={{ 
      flex: 1,
      background: 'white', 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
      maxWidth: '100%',
      width: '100%',
      minWidth: 0
    }}>
      {/* Action Bar - only show if onBack is provided (not in tabbed interface) */}

        {/* Header for upload mode */}
        {uploadMode && (
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #f1f5f9',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '600',
                color: '#111827'
              }}>
                Import Creators
              </h1>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                Upload an Excel or CSV file to import creators into your workspace
              </p>
            </div>
            <button
              onClick={onBack}
              style={{
                padding: '8px 16px',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <XIcon style={{ width: '16px', height: '16px' }} />
              Close
            </button>
          </div>
        )}

        <ContentContainer>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
            {/* Mapping Mode Action Buttons - Above Table */}
            {showMappingResults && (
              <div style={{ 
                padding: '16px 24px',
                borderBottom: '1px solid #f1f5f9',
                background: 'white',
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <div style={{ color: '#6b7c93', fontSize: '14px' }}>
                  Review the mapping results below and click "Create Table" to import selected creators.
                </div>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => {
                      setShowMappingResults(false);
                      setMappingResults([]);
                      setUploadStep('waiting');
                      setColumns([]);
                      setRows([]);
                      setTableData({});
                    }}
                    style={{
                      padding: '8px 16px',
                      background: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      color: '#374151'
                    }}
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={() => {
                      const selectedEntries = mappingResults.filter(entry => 
                        entry.tiktok_data && entry.tiktok_data.length > 0
                      );
                      handleCreateTableWithMappedCreators(selectedEntries);
                    }}
                    disabled={mappingResults.filter(entry => entry.tiktok_data && entry.tiktok_data.length > 0).length === 0}
                    style={{
                      padding: '8px 16px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      opacity: mappingResults.filter(entry => entry.tiktok_data && entry.tiktok_data.length > 0).length === 0 ? 0.5 : 1
                    }}
                  >
                    Create Table ({mappingResults.filter(entry => entry.tiktok_data && entry.tiktok_data.length > 0).length} creators)
                  </button>
                </div>
              </div>
            )}

            {/* Render research table view */}
            <ResearchTableView
                columns={columns}
                rows={rows}
                tableData={tableData}
                editingColumnId={null}
                onResearchCell={handleResearchCell}
                onResearchAllInColumn={handleResearchAllInColumn}
                onResearchAllInRow={handleResearchAllInRow}
                onResearchEmptyInRow={handleResearchEmptyInRow}
                onResearchAllRows={handleResearchAllRows}
                onEditColumn={handleEditColumn}
                onSaveColumn={handleSaveColumn}
                onDeleteColumn={handleDeleteColumn}
                onCancelEdit={() => {}}
                onAddColumn={handleAddColumn}
                onReorderColumns={handleReorderColumns}
          onHideColumn={async (columnId) => {
            // Save to backend and reload
            await saveHiddenColumnsToBackend(new Set([...Array.from(hiddenColumns), columnId]));
            if (tableId) loadExistingTable(tableId);
          }}
                onExportCSV={handleExportCSV}
                onCopyToClipboard={handleCopyToClipboard}
                onRefreshTable={() => tableId && loadExistingTable(tableId)}
                onHiddenColumnsSave={async (newHiddenColumns) => {
                  console.log('🔄 [ResearchTable] onHiddenColumnsSave called with:', Array.from(newHiddenColumns));
                  await saveHiddenColumnsToBackend(newHiddenColumns);
                  console.log('🔄 [ResearchTable] About to reload table...');
                  if (tableId) {
                    console.log('🔄 [ResearchTable] Loading table:', tableId);
                    await loadExistingTable(tableId);
                  } else {
                    console.error('❌ [ResearchTable] No tableId to reload!');
                  }
                }}
                onHiddenColumnsChange={setHiddenColumns}
                tableName={currentTable?.name}
                tableId={tableId} // Pass tableId for context management
                projectId={projectId} // Pass projectId for adding creators to project
                onCreatorAdded={handleCreatorAdded} // Optimized: add single creator without full refresh
                getCreatorPrimaryPlatform={getCreatorPrimaryPlatform}
                onDeleteShopifyDiscountCode={handleDeleteShopifyDiscountCode}
                // Thread checking functions for communication cells
                isCommunicationCellDisabled={isCommunicationCellDisabled}
                isPlatformCellDisabled={isPlatformCellDisabled}
                creatorThreadStatus={creatorThreadStatus}
                
                // Creator editing
                onEditCreator={handleEditCreator}
                onAddCreatorManually={handleAddCreatorManually}
                onRemoveCreator={handleRemoveCreator}

                onUpdateCellValue={handleUpdateCellValue}
                onOpenCreatorConversation={handleOpenCreatorConversation}
                // Pending change handlers
                onAcceptPendingChange={handleAcceptPendingChange}
                onDismissPendingChange={handleDismissPendingChange}
                onOpenCellSidebar={handleOpenCellSidebar}
                
                // Cell details sidebar props
                cellSidebarOpen={cellSidebarOpen}
                onCloseCellSidebar={handleCloseCellSidebar}
                selectedCell={selectedCell}
                getCreatorRowData={getCreatorRowData}
                onVerifyPost={async (verification) => {
                  // Handle post verification/revert
                  if (!selectedCell || !tableId) return;
                  
                  try {
                    const actionText = verification === 'verified' 
                      ? 'Verifying' 
                      : verification === 'rejected' 
                        ? 'Rejecting' 
                        : 'Reverting';
                    
                    message.loading({ content: `${actionText} post...`, key: 'verify-post' });
                    
                    const response = await researchTableService.verifyPost(tableId, {
                      creator_id: selectedCell.creator?.id || '',
                      column_id: selectedCell.column?.id || '',
                      verification
                    });
                    
                    if (response.success) {
                      const successText = verification === 'verified' 
                        ? 'Post verified!' 
                        : verification === 'rejected' 
                          ? 'Post rejected' 
                          : 'Reverted to pending';
                      
                      message.success({ 
                        content: successText, 
                        key: 'verify-post',
                        duration: 2
                      });
                      
                      // Update the cell data with new verification status
                      if (selectedCell.creator?.id && selectedCell.column?.id) {
                        handleUpdateCellValue(
                          selectedCell.creator.id,
                          selectedCell.column.id,
                          JSON.stringify(response.cellData)
                        );
                      }
                      
                      // Keep sidebar open when reverting, close when verifying/rejecting
                      if (verification === 'pending_verification') {
                        // Refresh cell data in sidebar
                        setSelectedCell({
                          ...selectedCell,
                          data: { ...selectedCell.data, value: JSON.stringify(response.cellData) }
                        });
                      } else {
                        // Close sidebar after successful verification/rejection
                        handleCloseCellSidebar();
                      }
                    }
                  } catch (error) {
                    console.error('Verification error:', error);
                    message.error({ content: 'Failed to verify post', key: 'verify-post' });
                  }
                }}
                confirmOverwritePopover={confirmOverwritePopover}
                onConfirmOverwrite={handleConfirmOverwrite}
                onCancelOverwrite={() => setConfirmOverwritePopover(null)}
                
                // Table context (pass table type from currentTable)
                tableType={parseTableType(currentTable?.table_type)}
                tableContext={currentTable?.table_config?.table_context}
                tableGoal={currentTable?.table_config?.table_goal}
                onUpdateTableContext={handleUpdateTableContext}
                onRunAllEmpty={handleResearchUnrunRows}
                onOpenTemplates={handleOpenTemplates}
                
                // Creator outreach drawer props
                outreachDrawerOpen={outreachSidebarOpen}
                outreachDrawerContent={
                  selectedCreator ? (
                    <div style={{ 
                      padding: '20px', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      color: '#6b7280'
                    }}>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500' }}>
                        Creator Outreach
                      </h3>
                      <p style={{ margin: '0', fontSize: '14px', textAlign: 'center' }}>
                        Outreach functionality for {selectedCreator.creator_shipping?.name || 'this creator'} will be available soon.
                      </p>
                      <button
                        onClick={handleCloseOutreachSidebar}
                        style={{
                          marginTop: '16px',
                          padding: '8px 16px',
                          background: '#f3f4f6',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Close
                      </button>
                    </div>
                  ) : null
                }
                // Tab bar props
                projectTables={projectTables}
                activeTableId={activeTableId}
                onTabClick={onTabClick}
                onDeleteTable={onDeleteTable}
                onCreateTable={onCreateTable}
                showTabBar={showTabBar}
                uploadMode={uploadMode}
                searchMode={searchMode}
                librarySidebarOpen={librarySidebarOpen}
                sidebarCollapsed={sidebarCollapsed}
                mainSidebarExpanded={mainSidebarExpanded}
                
                // Companion settings
                currentTable={currentTable || undefined}
                onUpdateCompanionSettings={handleUpdateCompanionSettings}
                
                // Workflow analysis queue status
                queueStatus={queueStatus}
              />
          </div>
      </ContentContainer>

      <CreatorModal
        isOpen={creatorModalOpen}
        onClose={handleCloseCreatorModal}
        onSuccess={handleCreatorModalSuccess}
        mode="edit"
        creator={editingCreator}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        title="Remove Creator from Project"
        open={deleteConfirmOpen}
        onOk={handleConfirmDeleteCreator}
        onCancel={handleCancelDeleteCreator}
        okText="Remove"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to remove <strong>{creatorToDelete?.tiktok_data?.[0]?.handle || creatorToDelete?.instagram_data?.[0]?.handle || creatorToDelete?.twitch_data?.[0]?.handle || 'this creator'}</strong> from this project?
        </p>
        <p style={{ marginTop: '12px', color: '#6b7c93' }}>
          This will remove all their data from the research tables in this project.
        </p>
      </Modal>
    </div>
  );
};
